(() => {
  'use strict';
  const ELEMENT_NAME = 's-dilution-calculator';
  if (customElements.get(ELEMENT_NAME)) return;
  class DilutionCalculator extends HTMLElement {
    connectedCallback() {
      if (this.__dilutionCalculatorInitialised) return;
      this.__dilutionCalculatorInitialised = true;
      const componentRoot = this;
      /* Unit model. Every concentration is reduced to a base unit PER MILLILITRE
         inside its own family. Cross-family maths is blocked, not guessed. */
      var UNITS = [
        {id:"mgml",   lab:"mg/mL",     fam:"mass", f:1,      grp:"Weight in volume"},
        {id:"pct",    lab:"% (w/v)",   fam:"mass", f:10,     grp:"Weight in volume"},
        {id:"mcgml",  lab:"mcg/mL",    fam:"mass", f:0.001,  grp:"Weight in volume"},
        {id:"gl",     lab:"g/L",       fam:"mass", f:1,      grp:"Weight in volume"},
        {id:"mgl",    lab:"mg/L",      fam:"mass", f:0.001,  grp:"Weight in volume"},
        {id:"gml",    lab:"g/mL",      fam:"mass", f:1000,   grp:"Weight in volume"},
        {id:"ratio",  lab:"1:X ratio", fam:"mass", ratio:true, grp:"Weight in volume"},
        {id:"meqml",  lab:"mEq/mL",    fam:"meq",  f:1,      grp:"Electrolyte"},
        {id:"meql",   lab:"mEq/L",     fam:"meq",  f:0.001,  grp:"Electrolyte"},
        {id:"mmolml", lab:"mmol/mL",   fam:"mmol", f:1,      grp:"Electrolyte"},
        {id:"mmoll",  lab:"mmol/L",    fam:"mmol", f:0.001,  grp:"Electrolyte"},
        {id:"iuml",   lab:"IU/mL",     fam:"iu",   f:1,      grp:"Units of activity"},
        {id:"iul",    lab:"IU/L",      fam:"iu",   f:0.001,  grp:"Units of activity"}
      ];
      var UMAP = {}; for (var i=0;i<UNITS.length;i++) UMAP[UNITS[i].id] = UNITS[i];

      /* amount (not per mL) units, used for "put this much drug in the bag" */
      var AMTS = [
        {id:"mg",   lab:"mg",   fam:"mass", f:1},
        {id:"g",    lab:"g",    fam:"mass", f:1000},
        {id:"mcg",  lab:"mcg",  fam:"mass", f:0.001},
        {id:"meq",  lab:"mEq",  fam:"meq",  f:1},
        {id:"mmol", lab:"mmol", fam:"mmol", f:1},
        {id:"iu",   lab:"IU",   fam:"iu",   f:1}
      ];
      var AMAP = {}; for (var j=0;j<AMTS.length;j++) AMAP[AMTS[j].id] = AMTS[j];

      /* per-kg dose units */
      var DOSES = [
        {id:"mgkg",   lab:"mg/kg",   fam:"mass", f:1},
        {id:"mcgkg",  lab:"mcg/kg",  fam:"mass", f:0.001},
        {id:"gkg",    lab:"g/kg",    fam:"mass", f:1000},
        {id:"meqkg",  lab:"mEq/kg",  fam:"meq",  f:1},
        {id:"mmolkg", lab:"mmol/kg", fam:"mmol", f:1},
        {id:"iukg",   lab:"IU/kg",   fam:"iu",   f:1}
      ];
      var DMAP = {}; for (var k=0;k<DOSES.length;k++) DMAP[DOSES[k].id] = DOSES[k];

      var BASELAB = {mass:"mg", meq:"mEq", mmol:"mmol", iu:"IU"};
      var FAMLAB  = {mass:"weight (mg, mcg, g, % or ratio)", meq:"milliequivalents", mmol:"millimoles", iu:"international units"};

      /* value + unit id -> base units per mL */
      function toBase(val, uid){
        var u = UMAP[uid];
        if (!u) return NaN;
        if (u.ratio) return (val > 0) ? 1000 / val : NaN;   /* 1:1000 = 1 g in 1000 mL = 1 mg/mL */
        return val * u.f;
      }
      /* base units per mL -> value in unit id */
      function fromBase(base, uid){
        var u = UMAP[uid];
        if (!u) return NaN;
        if (u.ratio) return (base > 0) ? 1000 / base : NaN;
        return base / u.f;
      }
      function famOf(uid){ return UMAP[uid] ? UMAP[uid].fam : null; }

      /* 4 significant figures, no exponent soup, thousands separated */
      function fmt(n){
        if (n === null || n === undefined || !isFinite(n)) return "—";
        if (n === 0) return "0";
        var a = Math.abs(n);
        if (a >= 1000) return Math.round(n).toLocaleString("en-US");
        if (a < 0.000001){
          /* never exponent notation: "1.2e-7 mL" is not a clinical instruction */
          var d = Math.min(20, Math.max(0, 3 - Math.floor(Math.log(a) / Math.LN10)));
          return n.toFixed(d).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
        }
        return String(Number(n.toPrecision(4)));
      }
      /* read a formatted string back as a number so a working line can be
         computed from exactly the digits it displays */
      function dnum(s){ return parseFloat(String(s).replace(/,/g, "")); }

      /* ratio strengths are written the way drug labels write them:
         1:1000, then comma separated from ten thousand up */
      function fmtRatio(x){
        if (!isFinite(x)) return "—";
        if (Math.abs(x) >= 10000) return Math.round(x).toLocaleString("en-US");
        if (Math.abs(x) >= 1) return String(Number(x.toPrecision(6)));
        return fmt(x);
      }

      /* amount in base units -> readable string with a sensible prefix */
      function fmtAmt(base, fam){
        if (!isFinite(base)) return "—";
        if (fam === "mass"){
          if (base >= 1000) return fmt(base / 1000) + " g";
          if (base < 1 && base > 0) return fmt(base * 1000) + " mcg";
          return fmt(base) + " mg";
        }
        return fmt(base) + " " + BASELAB[fam];
      }
      /* concentration in base-per-mL -> readable string in that family */
      function fmtConc(base, fam){
        if (!isFinite(base)) return "—";
        if (fam === "mass"){
          /* switch to mcg only below 0.1 mg/mL. 0.1 mg/mL is how adrenaline
             1:10,000 is spoken about, and restating it as 100 mcg/mL invites
             exactly the tenfold slip this tool exists to prevent. */
          if (base < 0.1 && base > 0) return fmt(base * 1000) + " mcg/mL";
          return fmt(base) + " mg/mL";
        }
        return fmt(base) + " " + BASELAB[fam] + "/mL";
      }
      function fmtConcPrecise(base, fam){
        if (!isFinite(base)) return "—";
        var more = function(n){
          var a = Math.abs(n);
          if (a > 0 && a < 0.000001) return n.toFixed(Math.min(20, Math.max(0, 6 - Math.floor(Math.log(a) / Math.LN10)))).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
          return String(Number(n.toPrecision(7)));
        };
        if (fam === "mass" && base < 0.1 && base > 0) return more(base * 1000) + " mcg/mL";
        return more(base) + " " + BASELAB[fam] + "/mL";
      }

      /* Each preset carries: an explicit drug label for the syringe label (never
         derived from the name, because "Calcium 50 mg/mL" does not say gluconate),
         a severity level for its note, and an optional solute tag so the tool can
         catch the case where the diluent already contains the same substance. */
      var PRESETS = [
       {id:"adr10k", g:"Emergency and CPR", n:"Adrenaline 1:1000 to 1:10,000", drug:"Adrenaline (epinephrine)",
        c1:[1000,"ratio"], c2:[10000,"ratio"], v2:10, lvl:"amber", diluent:"0.9% sodium chloride", allowedDiluents:["0.9% sodium chloride"],
        why:"Low-dose IV adrenaline in CPR and accurate dosing in small patients",
        note:"1:1000 is 1 mg/mL and 1:10,000 is 0.1 mg/mL. Label the syringe the moment you make it: the two strengths are indistinguishable once drawn up, and giving the neat product where the diluted was intended is a tenfold overdose. Never run adrenaline through a line that has just had sodium bicarbonate in it without flushing: bicarbonate inactivates catecholamines."},
       {id:"atropine", g:"Emergency and CPR", n:"Atropine 0.6 mg/mL to 0.06 mg/mL", drug:"Atropine sulfate",
        c1:[0.6,"mgml"], c2:[0.06,"mgml"], v2:10, lvl:"info",
        why:"Neonates, small exotics and birds",
        note:"At 0.02 mg/kg a 300 g patient needs 0.01 mL of the neat product, which cannot be measured in a 1 mL syringe. Atropine is sold at 0.6 mg/mL, 0.54 mg/mL, 0.4 mg/mL and 1 mg/mL depending on market: read the strength off the vial, not off this preset."},
       {id:"bicarb", g:"Emergency and CPR", n:"Sodium bicarbonate 8.4% to 4.2%", drug:"Sodium bicarbonate",
        c1:[8.4,"pct"], c2:[4.2,"pct"], v2:20, lvl:"amber", diluent:"Sterile water for injection", allowedDiluents:["Sterile water for injection"],
        why:"Neonates and small patients, where undiluted 8.4% is too hyperosmolar",
        note:"Dilute with sterile water, not with Hartmann's. Bicarbonate precipitates with calcium-containing fluids and inactivates adrenaline and other catecholamines, so it must never share a line with either without a flush between. Never give it perivascularly."},
       {id:"dex50", g:"Emergency and CPR", n:"Dextrose 50% to 12.5%", drug:"Dextrose (glucose)",
        c1:[50,"pct"], c2:[12.5,"pct"], v2:20, lvl:"amber", solute:"dextrose",
        why:"Hypoglycaemia bolus, diluted 1 in 4",
        note:"Give 0.5 to 1.5 mL/kg of the 50% product diluted at least 1 in 4, slowly, into a patent and well-flushed catheter. Do not run dextrose above 5% as a CRI in a peripheral vein. Undiluted 50% dextrose is severely sclerosing and causes necrosis if it extravasates."},
       {id:"cagluc", g:"Emergency and CPR", n:"Calcium gluconate 10% to 5%", drug:"Calcium gluconate",
        c1:[10,"pct"], c2:[5,"pct"], v2:20, lvl:"amber", basis:"calcium gluconate salt (not elemental calcium)", diluent:"0.9% sodium chloride", allowedDiluents:["0.9% sodium chloride","5% dextrose"],
        why:"Concentrations are calcium gluconate salt, not elemental calcium",
        note:"10% calcium gluconate is 9.3 mg/mL elemental calcium; 10% calcium chloride is 27.2 mg/mL. The two are not interchangeable despite both reading 10%. Give 0.5 to 1.5 mL/kg of 10% gluconate slowly with continuous ECG and stop for bradycardia or an arrhythmia. Calcium is severely irritant perivascularly."},
       {id:"hyper", g:"Emergency and CPR", n:"Hypertonic saline 23.4% to 7.2%", drug:"Hypertonic sodium chloride",
        c1:[23.4,"pct"], c2:[7.2,"pct"], v2:100, lvl:"amber", solute:"nacl", diluent:"Sterile water for injection", allowedDiluents:["Sterile water for injection","0.9% sodium chloride"],
        why:"Solute-free compatible diluent only; saline requires background correction",
        note:"Dose 2 to 5 mL/kg of the finished 7.2% solution IV over 10 to 15 minutes and never faster than 1 mL/kg/min. Contraindicated in dehydration, hypernatraemia and uncontrolled haemorrhage. In chronic hyponatraemia, correcting sodium faster than 0.5 mEq/L/hr risks osmotic demyelination. Follow with an isotonic crystalloid, and check whether a ready-made 7.2% product is on the shelf before compounding one."},

       {id:"naloxone", g:"Emergency and CPR", n:"Naloxone 0.4 mg/mL to 0.04 mg/mL", drug:"Naloxone",
        c1:[0.4,"mgml"], c2:[0.04,"mgml"], v2:10, lvl:"info",
        why:"Titrated opioid reversal after ventilation and resuscitation are established",
        note:"Ventilation and standard resuscitation come first; use naloxone only when opioid exposure is known or strongly suspected. Titrate to effect rather than giving the whole calculated dose: full reversal takes the analgesia with it. Naloxone is shorter acting than most opioids, so watch for renarcotisation."},

       {id:"ketamine", g:"Anaesthesia and analgesia", n:"Ketamine 100 mg/mL to 10 mg/mL", drug:"Ketamine",
        c1:[100,"mgml"], c2:[10,"mgml"], v2:10, lvl:"info",
        why:"Small doses, top-ups and syringe driver loading",
        note:"Controlled drug in most jurisdictions. Record the dilution and any discard volume in the register."},
       {id:"bupren", g:"Anaesthesia and analgesia", n:"Buprenorphine 0.3 mg/mL to 0.03 mg/mL", drug:"Buprenorphine",
        c1:[0.3,"mgml"], c2:[0.03,"mgml"], v2:10, lvl:"amber",
        why:"Cats and small dogs",
        note:"Controlled drug: two-person check and a register entry for both the dilution and the discard. Do not confuse the 0.3 mg/mL product with concentrated buprenorphine (Simbadol, 1.8 mg/mL), a sixfold difference, or with transdermal Zorbium (20 mg/mL), which is never injected."},
       {id:"methadone", g:"Anaesthesia and analgesia", n:"Methadone 10 mg/mL to 1 mg/mL", drug:"Methadone",
        c1:[10,"mgml"], c2:[1,"mgml"], v2:10, lvl:"info",
        why:"Accurate dosing in cats and small dogs",
        note:"Controlled drug. Two-person check and register entry."},
       {id:"butorph", g:"Anaesthesia and analgesia", n:"Butorphanol 10 mg/mL to 1 mg/mL", drug:"Butorphanol",
        c1:[10,"mgml"], c2:[1,"mgml"], v2:10, lvl:"info",
        why:"Cats, small dogs and exotics",
        note:"Sedation and antitussive effect outlast the analgesia, which is short and ceiling-limited. Do not rely on it as the sole analgesic for anything painful."},
       {id:"dexmed", g:"Anaesthesia and analgesia", n:"Dexmedetomidine 0.5 mg/mL to 0.05 mg/mL", drug:"Dexmedetomidine",
        c1:[0.5,"mgml"], c2:[0.05,"mgml"], v2:10, lvl:"info",
        why:"Microdoses and sedation top-ups",
        note:"At 1 mcg/kg a 4 kg cat needs 0.008 mL of the neat product. Diluting tenfold makes it 0.08 mL, which is still small: consider a hundredfold dilution for patients under 2 kg."},
       {id:"medet", g:"Anaesthesia and analgesia", n:"Medetomidine 1 mg/mL to 0.1 mg/mL", drug:"Medetomidine",
        c1:[1,"mgml"], c2:[0.1,"mgml"], v2:10, lvl:"amber",
        why:"Microdoses and sedation top-ups",
        note:"Medetomidine is racemic and only the dexmedetomidine half is active, so 2 mcg/kg of medetomidine is roughly equivalent to 1 mcg/kg of dexmedetomidine. Zenalpha is a different product again: medetomidine 0.5 mg/mL with vatinoxan, half the strength of Domitor, and it must not be diluted or mixed."},
       {id:"fentanyl", g:"Anaesthesia and analgesia", n:"Fentanyl 50 mcg/mL to 5 mcg/mL", drug:"Fentanyl",
        c1:[50,"mcgml"], c2:[5,"mcgml"], v2:10, lvl:"amber",
        why:"Small patients and fine titration",
        note:"Controlled drug. Fentanyl is potent enough that a tenfold error is a respiratory arrest: label the syringe and check it with a second person."},
       {id:"midaz", g:"Anaesthesia and analgesia", n:"Midazolam 5 mg/mL to 1 mg/mL", drug:"Midazolam",
        c1:[5,"mgml"], c2:[1,"mgml"], v2:10, lvl:"info",
        why:"Co-induction and seizure dosing in small patients",
        note:"Controlled drug in many jurisdictions. Midazolam is sold at 1, 2 and 5 mg/mL: read the ampoule. Diazepam is not a substitute here, as it precipitates in aqueous diluents and adsorbs to PVC."},
       {id:"ace", g:"Anaesthesia and analgesia", n:"Acepromazine 2 mg/mL to 0.2 mg/mL", drug:"Acepromazine",
        c1:[2,"mgml"], c2:[0.2,"mgml"], v2:10, lvl:"amber",
        why:"Low-dose titration, which is how acepromazine should be used",
        note:"Check the vial: acepromazine injection is 2 mg/mL in Australia, the UK and the EU, and 10 mg/mL in the United States, a fivefold difference with no reversal agent. Doses are far lower than the label suggests, around 0.005 to 0.02 mg/kg with a total dose cap, and it has no analgesic effect. Diluting it is routine but off-label, so record it."},
       {id:"glyco", g:"Anaesthesia and analgesia", n:"Glycopyrrolate 0.2 mg/mL to 0.02 mg/mL", drug:"Glycopyrrolate",
        c1:[0.2,"mgml"], c2:[0.02,"mgml"], v2:10, lvl:"info",
        why:"Exotics and small patients when the exact formulation is suitable",
        note:"Slower in onset than atropine, with limited transfer across the blood-brain barrier and placenta. Check excipients: some 0.2 mg/mL products contain benzyl alcohol and are unsuitable for neonates, and some formulations are incompatible with Lactated Ringer's. Use only a product-compatible diluent."},
       {id:"insulin", g:"Endocrine and metabolic", n:"Regular (soluble) insulin U-100 to U-10", drug:"Regular (soluble) insulin",
        c1:[100,"iuml"], c2:[10,"iuml"], v2:10, lvl:"red", basis:"insulin units", diluent:"Other diluent", otherDiluent:"", allowedDiluents:["Other diluent"], requiredOther:true, requiredOtherLabel:"the exact manufacturer-specified insulin diluent name",
        why:"Small-patient dosing only when the exact product label permits dilution",
        note:"This is a U-10 subcutaneous-dilution calculation, not an insulin CRI recipe. Use only neutral, regular, soluble insulin and the exact manufacturer-specified diluent, storage window and syringe plan. Never dilute glargine, detemir, PZI, lente or NPH unless that exact product label explicitly permits it. Check U-40, U-100 or U-300 on the vial and independently verify every conversion. Diluted insulin is dangerously easy to confuse with the neat product."},

       {id:"lido1", g:"Local anaesthetics", n:"Lidocaine 2% to 1%", drug:"Lidocaine",
        c1:[2,"pct"], c2:[1,"pct"], v2:10, lvl:"amber",
        why:"Larger-volume local blocks without exceeding the toxic dose",
        note:"Keep total lidocaine under about 6 mg/kg in dogs and 2 mg/kg in cats, counting every route. Some references allow more; these are deliberately conservative ceilings."},
       {id:"lido05", g:"Local anaesthetics", n:"Lidocaine 2% to 0.5%", drug:"Lidocaine",
        c1:[2,"pct"], c2:[0.5,"pct"], v2:20, lvl:"amber",
        why:"Splash blocks, line blocks in cats and volume-limited blocks",
        note:"Diluting lets you cover a longer incision with the same total milligrams. Keep total lidocaine under about 6 mg/kg in dogs and 2 mg/kg in cats, counting every route. Use preservative-free product for any block near the neuraxis."},
       {id:"bupi", g:"Local anaesthetics", n:"Bupivacaine 0.5% to 0.25%", drug:"Bupivacaine",
        c1:[0.5,"pct"], c2:[0.25,"pct"], v2:10, lvl:"amber",
        why:"Larger-volume infiltration and cats",
        note:"Applies to plain bupivacaine hydrochloride only. Keep the total under about 2 mg/kg in dogs and 1 mg/kg in cats, and never give bupivacaine intravenously: it is cardiotoxic and the arrest is hard to reverse. Liposomal bupivacaine (Nocita, 13.3 mg/mL) is a different product: the dog label permits up to 1:1 dilution with 0.9% saline or Hartmann's, while the cat nerve-block label says not to dilute. Never use water or mix it with other local anaesthetics."},

       {id:"kcl", g:"Fluids and electrolytes", n:"Potassium chloride into a 1 L bag at 20 mEq/L", drug:"Potassium chloride",
        mode:"bag", bag:1000, c1:[2,"meqml"], c2:[20,"meql"], lvl:"red", diluent:"0.9% sodium chloride", allowedDiluents:["0.9% sodium chloride","5% dextrose","Lactated Ringer's (Hartmann's)","Other diluent"],
        why:"Confirm actual bag volume; Hartmann's requires its potassium-background correction",
        note:"Never bolus potassium and never give it undiluted: the concentrate ampoule sits next to sodium chloride in most crash trolleys and the two look alike. Remove the same volume from the bag before adding, invert the bag several times so it does not sit in a layer, keep the delivered rate under 0.5 mEq/kg/hr, and keep peripheral infusions at or below about 60 mEq/L. Concentrates are sold at 1 mEq/mL and 2 mEq/mL: read the ampoule."},
       {id:"oxytocin", g:"Fluids and electrolytes", n:"Oxytocin 10 IU/mL to 1 IU/mL", drug:"Oxytocin",
        c1:[10,"iuml"], c2:[1,"iuml"], v2:10, lvl:"amber",
        why:"Small-patient uterine inertia dosing",
        note:"Low doses work better than high ones and repeated high doses cause tetanic contraction. Never give oxytocin before obstruction has been ruled out."},
       {id:"heparin", g:"Fluids and electrolytes", n:"Heparinised saline 1000 IU/mL to 10 IU/mL", drug:"Heparin in saline",
        c1:[1000,"iuml"], c2:[10,"iuml"], v2:100, lvl:"info", use:"Other use",
        why:"Catheter flush solution",
        note:"Heparin is sold at 1000 IU/mL and 5000 IU/mL: read the vial. Many practices now flush with plain saline instead. Never use a heparinised flush bag as a fluid source."},

       {id:"chx", g:"Antiseptics and lavage", n:"Chlorhexidine 2% aqueous to 0.05%", drug:"Chlorhexidine (aqueous)",
        c1:[2,"pct"], c2:[0.05,"pct"], v2:500, lvl:"red", use:"Topical use or wound lavage", routeLocked:true, noInjection:true,
        why:"Open wound lavage",
        note:"Start from a 2% aqueous solution, never from a 4% surgical scrub: detergent must not touch an open wound. Chlorhexidine at any concentration must not enter the peritoneal cavity, the bladder, a joint, the eye beyond a 0.05% pre-operative prep, or a middle ear with a ruptured tympanum, because it causes chemical peritonitis, cystitis, synovitis and deafness."},
       {id:"pvi", g:"Antiseptics and lavage", n:"Povidone-iodine 10% to 1%", drug:"Povidone-iodine",
        c1:[10,"pct"], c2:[1,"pct"], v2:500, lvl:"red", use:"Topical use or wound lavage", routeLocked:true, noInjection:true,
        why:"Open wound lavage",
        note:"Use 0.1% to 1% on open wounds, and never the scrub or detergent form. Do not use povidone-iodine, or any antiseptic, for peritoneal, thoracic or joint lavage: use warm isotonic crystalloid alone. Full-strength povidone-iodine is cytotoxic to fibroblasts and delays healing, and iodine absorbed from a large wound can cause thyroid suppression and metabolic acidosis."},

       {id:"iohexol", g:"Diagnostics and other", n:"Iohexol 350 to 240 mg iodine/mL", drug:"Iohexol",
        c1:[350,"mgml"], c2:[240,"mgml"], v2:50, lvl:"amber", basis:"iodine", use:"Diagnostic or laboratory use", diluent:"Sterile water for injection",
        why:"Gastrointestinal and cystographic contrast studies",
        note:"Concentrations are milligrams of iodine per mL. Do not dilute for myelography: use the labelled myelographic product and concentration."}
      ];
      /* what the standard diluents already contain, so the tool can catch the
         case of diluting a solute with a fluid that already holds that solute */
      var DILUENT_SOLUTE = {
        "0.9% sodium chloride":      {solute:"nacl",     conc:9,  val:0.9, unit:"pct",  label:"0.9% sodium chloride is already 9 mg/mL of sodium chloride"},
        "Lactated Ringer's (Hartmann's)": {solute:"nacl", conc:6, val:6, unit:"mgml", label:"Hartmann's already contains about 6 mg/mL of sodium chloride"},
        "5% dextrose":               {solute:"dextrose", conc:50, val:5, unit:"pct", label:"5% dextrose is already 50 mg/mL of dextrose"}
      };

      /* ---------------------------------------------------------------
         Pure calculation core. No DOM. Exported so the same functions can
         be run outside the browser during verification.
         All concentrations are in base units per mL, all volumes in mL.
         --------------------------------------------------------------- */
      function calcQuick(c1, c2, v2, c0){
        c0 = isFinite(c0) ? c0 : 0;
        var v1 = (c2 - c0) * v2 / (c1 - c0);
        var diluent = v2 - v1;
        var stockAmount = c1 * v1, diluentAmount = c0 * diluent;
        var total = stockAmount + diluentAmount;
        return {v1:v1, diluent:diluent, finalVol:v2, finalConc:total / v2,
                factor:v2 / v1, total:total, stockAmount:stockAmount, diluentAmount:diluentAmount, c0:c0};
      }
      function calcBag(opts){
        /* opts: c1, c0, bag, method ("remove"|"add"), target ("conc"|"mass"), c2, mass */
        var vd, finalVol, total, c0 = isFinite(opts.c0) ? opts.c0 : 0;
        if (opts.target === "mass"){
          total = opts.mass;
          vd = (opts.method === "add") ? (total - c0 * opts.bag) / opts.c1
                                        : (total - c0 * opts.bag) / (opts.c1 - c0);
          finalVol = (opts.method === "add") ? opts.bag + vd : opts.bag;
        } else {
          if (opts.method === "add"){
            vd = (opts.c2 - c0) * opts.bag / (opts.c1 - opts.c2);
            finalVol = opts.bag + vd;
          } else {
            vd = (opts.c2 - c0) * opts.bag / (opts.c1 - c0);
            finalVol = opts.bag;
          }
        }
        var diluent = finalVol - vd;
        var stockAmount = opts.c1 * vd, diluentAmount = c0 * diluent;
        if (opts.target !== "mass") total = stockAmount + diluentAmount;
        return {v1:vd, diluent:diluent, finalVol:finalVol, finalConc:total / finalVol,
                factor:finalVol / vd, total:total, stockAmount:stockAmount, diluentAmount:diluentAmount, c0:c0,
                removeVol:(opts.method === "remove") ? vd : 0};
      }
      function calcCheck(c1, v1, dil, c0){
        c0 = isFinite(c0) ? c0 : 0;
        var finalVol = v1 + dil;
        var stockAmount = c1 * v1, diluentAmount = c0 * dil;
        var total = stockAmount + diluentAmount;
        return {v1:v1, diluent:dil, finalVol:finalVol, finalConc:total / finalVol,
                factor:finalVol / v1, total:total, stockAmount:stockAmount, diluentAmount:diluentAmount, c0:c0};
      }
      /* Build repeated 1-in-10 steps instead of an impractical 1 mL + 999 mL
         intermediate. drawForFactor lets bag add-on-top mode solve its exact formula. */
      function serialPlan(v1, minVol, maxStages, drawForFactor, maxDraw){
        var k = 1, i, draw;
        for (i = 1; i <= maxStages; i++){
          k *= 10;
          draw = drawForFactor ? drawForFactor(k) : v1 * k;
          if (!isFinite(draw) || draw <= 0) continue;
          if (isFinite(maxDraw) && draw > maxDraw + 1e-10) break;
          if (draw >= minVol) return {k:k, factor:k, stages:i, draw:draw};
        }
        return null;
      }
      var SYRINGES = [0.3, 0.5, 1, 3, 5, 10, 20, 30, 60];
      function syringeFor(v){
        for (var i = 0; i < SYRINGES.length; i++){ if (v <= SYRINGES[i] + 1e-12) return SYRINGES[i]; }
        return null;
      }

      /* =====================================================================
         ASK A VET(TM) DILUTION CALCULATOR  v2
         Deterministic arithmetic only. Nothing here guesses.

         v1 -> v2, driven by three independent audits:
         1. FIXED: the "Check a dilution" headline multiplied any concentration
            below 1 by 1000 but only relabelled it for mass units, so a potassium
            bag check read 20 mEq/mL instead of 0.02 mEq/mL, a thousandfold error
            against the tool's own printed record.
         2. FIXED: the serial dilution plan existed only on screen. The printed
            and copied record instructed an unmeasurable volume and pointed to
            steps that were never printed. Steps are now built once and shared by
            the screen, the record and the print sheet.
         3. FIXED: bag "add on top" reported the whole existing bag volume as
            "diluent to add".
         4. FIXED: the dose check kept a stale volume on screen after the
            calculation became blocked.
         5. FIXED: a preset kept its drug name and its drug-specific safety note
            after the user replaced every number, and carried them onto the
            printed label and the shared link.
         6. FIXED: red warnings now render ABOVE the headline, and an unmeasurable
            volume leads with the serial dilution rather than the impossible one.
         7. ADDED: warning when the chosen diluent already contains the same
            solute (diluting hypertonic saline with saline, dextrose with dextrose),
            with the corrected volume.
         ===================================================================== */
      (function(){
      "use strict";
      var root = componentRoot;
      var el = function(id){ return root.querySelector("#" + id); };
      var LB = 2.2046226218;
      var DEFAULT_MIN_MEASURE = 0.05;
      var mode = "quick", bagMethod = "remove", bagTarget = "conc", wtU = "kg";
      var curPreset = null, presetSig = null, hashTimer = null, srTimer = null, booting = true;
      var S = {};

      /* ---------- helpers ---------- */
      function num(id){ var v = parseFloat(el(id).value); return isFinite(v) ? v : NaN; }
      function minMeasure(){ var v = num("minMeasure"); return isFinite(v) && v > 0 ? v : DEFAULT_MIN_MEASURE; }
      function measureIncrement(){ var v = num("measureIncrement"); return isFinite(v) && v > 0 ? v : 0.01; }
      function effectiveDiluent(){
        if (el("diluent").value !== "Other diluent") return el("diluent").value;
        return el("otherDiluent").value.trim();
      }
      function setActions(enabled){
        ["btnPrint","btnEmail","btnDownload","btnShare","btnCopy"].forEach(function(id){ el(id).disabled = !enabled; });
      }
      function segSet(wrapId, attr, val){
        var kids = el(wrapId).children, i;
        for (i = 0; i < kids.length; i++){
          var on = kids[i].getAttribute(attr) === val;
          kids[i].classList.toggle("active", on);
          kids[i].setAttribute("aria-pressed", on ? "true" : "false");
        }
      }
      function esc(s){ return String(s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;"); }
      function strip(s){ return String(s).replace(/<[^>]+>/g, ""); }
      function rank(l){ var r = {red:0, amber:1, info:2, ok:3}, v = r[l]; return (v === undefined) ? 2 : v; }
      function sortWarns(a){ return a.slice().sort(function(x,y){ return rank(x.lvl) - rank(y.lvl); }); }
      function warnHTML(list, dark){
        return sortWarns(list).map(function(w){
          var word = w.lvl === "red" ? "Critical. " : w.lvl === "amber" ? "Caution. " : w.lvl === "ok" ? "Clear. " : "Note. ";
          return '<div class="' + (dark ? "dwarn " : "warn ") + w.lvl + '"><b>' + word + "</b>" + w.t + "</div>";
        }).join("");
      }
      function toast(msg){
        var t = el("toast");
        t.textContent = msg; t.classList.remove("hide");
        clearTimeout(t._h); t._h = setTimeout(function(){ t.classList.add("hide"); }, 2200);
      }
      /* which solute the product is, so the tool can tell when the chosen diluent
         already contains it. Read from the drug name as well as the preset, because
         the preset is dropped as soon as the user edits a number. */
      function soluteOf(drugName, preset){
        var n = (drugName || "").toLowerCase();
        if (/sodium chloride|saline|nacl/.test(n)) return "nacl";
        if (/dextrose|glucose/.test(n)) return "dextrose";
        if (/potassium chloride|\bkcl\b/.test(n)) return "kcl";
        return preset ? preset.solute : null;
      }
      function presetById(id){ var f = null; PRESETS.forEach(function(x){ if (x.id === id) f = x; }); return f; }
      function sigForPreset(p){
        return [String(p.drug).trim().toLowerCase(), p.c1[0], p.c1[1], p.c2[0], p.c2[1]].join("~");
      }
      function presetDiluentOK(p, choice, effective){
        if (!p) return true;
        if (p.allowedDiluents && p.allowedDiluents.indexOf(choice) === -1) return false;
        if (p.requiredOther && (choice !== "Other diluent" || !effective || effective.length < 3)) return false;
        return true;
      }
      function allowedDiluentText(p){
        if (!p || !p.allowedDiluents) return "the product-compatible diluent required by the label or clinic protocol";
        return p.allowedDiluents.map(function(d){ return d === "Other diluent" && p.requiredOther ? (p.requiredOtherLabel || "an exact product-specific diluent name") : d; }).join(" or ");
      }
      function backgroundSolute(chosen, solute){
        if (chosen === "Lactated Ringer's (Hartmann's)" && solute === "kcl"){
          return {solute:"kcl", conc:0.004, val:4, unit:"meql", label:"Hartmann's already contains about 4 mEq/L of potassium"};
        }
        var known = DILUENT_SOLUTE[chosen];
        return known && known.solute === solute ? known : null;
      }
      function refreshDiluentFields(forceAuto){
        var chosen = el("diluent").value;
        el("otherDiluentWrap").classList.toggle("hide", chosen !== "Other diluent");
        var checkbox = el("sameSolute");
        var preset = presetById(curPreset);
        var solute = soluteOf(el("drugName").value.trim(), preset);
        var known = backgroundSolute(chosen, solute);
        var match = !!known;
        if (match && (forceAuto || checkbox.dataset.user !== "1")){
          checkbox.checked = true;
          checkbox.dataset.auto = "1";
          el("c0").value = known.val;
          el("c0u").value = known.unit;
        } else if (!match && checkbox.dataset.auto === "1" && checkbox.dataset.user !== "1"){
          checkbox.checked = false;
          checkbox.dataset.auto = "";
          el("c0").value = "";
        }
        el("diluentConcBlock").classList.toggle("hide", !checkbox.checked);
        ratioUI("c0Wrap", "c0", el("c0u").value === "ratio");
        el("c0Echo").textContent = checkbox.checked ? echoFor(num("c0"), el("c0u").value) : "";
      }

      /* volumes are never shown in exponent notation: a clinical instruction of
         "1.2e-7 mL" is not an instruction */
      function vol(n){
        if (!isFinite(n)) return "—";
        if (n > 0 && n < 0.0001) return "under 0.0001";
        return fmt(n);
      }

      /* ---------- unit selects ---------- */
      function fillUnitSelect(sel, list, selected){
        var groups = [], seen = {};
        list.forEach(function(u){ var g = u.grp || ""; if (!seen[g]){ seen[g] = []; groups.push(g); } seen[g].push(u); });
        var h = "";
        groups.forEach(function(g){
          if (g) h += '<optgroup label="' + esc(g) + '">';
          seen[g].forEach(function(u){ h += '<option value="' + u.id + '"' + (u.id === selected ? " selected" : "") + ">" + esc(u.lab) + "</option>"; });
          if (g) h += "</optgroup>";
        });
        sel.innerHTML = h;
      }
      function setupRatio(wrapId, inputId){
        var w = el(wrapId), s = root.ownerDocument.createElement("span");
        s.className = "one hide"; s.id = inputId + "One"; s.textContent = "1 :";
        s.setAttribute("aria-hidden", "true");
        w.insertBefore(s, w.firstChild);
      }
      function ratioUI(wrapId, inputId, isRatio){
        el(wrapId).className = isRatio ? "ratiowrap" : "";
        el(inputId + "One").classList.toggle("hide", !isRatio);
      }

      /* ---------- the unit converter echo ---------- */
      function echoFor(val, uid){
        var u = UMAP[uid];
        if (!u || !isFinite(val) || val <= 0) return "";
        var base = toBase(val, uid), fam = u.fam;
        if (fam !== "mass") return fmt(val) + " " + u.lab + " = " + fmtConc(base, fam);
        var lead = (uid === "ratio") ? ("1:" + fmtRatio(val)) : (uid === "pct") ? (fmt(val) + "%") : (fmt(val) + " " + u.lab);
        var alts = [];
        if (uid !== "mgml") alts.push(fmt(base) + " mg/mL");
        if (uid !== "pct") alts.push(fmt(base / 10) + "% w/v");
        if (base < 0.1 && uid !== "mcgml") alts.push(fmt(base * 1000) + " mcg/mL");
        if (!alts.length) return "";
        return lead + " = " + alts.join("  ·  ");
      }
      function unitEcho(v, uid){
        var u = UMAP[uid];
        if (u.ratio) return "1:" + fmtRatio(v);
        return fmt(v) + " " + u.lab;
      }

      /* ---------- presets ---------- */
      function renderPresets(){
        var q = el("presetSearch").value.trim().toLowerCase();
        var groups = [], byGroup = {};
        PRESETS.forEach(function(p){
          var hay = (p.n + " " + p.g + " " + p.why + " " + p.drug).toLowerCase();
          if (q && hay.indexOf(q) === -1) return;
          if (!byGroup[p.g]){ byGroup[p.g] = []; groups.push(p.g); }
          byGroup[p.g].push(p);
        });
        if (!groups.length){ el("presetList").innerHTML = '<div class="presetnone">No preset matches that. Type your own numbers below.</div>'; return; }
        var h = "";
        groups.forEach(function(g){
          h += '<div class="pgroup">' + esc(g) + "</div>";
          byGroup[g].forEach(function(p){
            h += '<button type="button" data-p="' + p.id + '"' + (curPreset === p.id ? ' class="active"' : "") +
                 "><b>" + esc(p.n) + "</b><span>" + esc(p.why) + "</span></button>";
          });
        });
        el("presetList").innerHTML = h;
        Array.prototype.forEach.call(el("presetList").querySelectorAll("button[data-p]"), function(b){
          b.addEventListener("click", function(){ applyPreset(b.getAttribute("data-p")); });
        });
      }
      /* A preset is identified by its two concentrations, and by nothing else.
         Changing the batch size, the bag volume or the mode does not change which
         drug you are holding, so the preset and its safety note survive those.
         Changing either concentration means it is no longer that preset. */
      function sigNow(){
        return [el("drugName").value.trim().toLowerCase(), el("c1").value, el("c1u").value, el("c2").value, el("c2u").value].join("~");
      }
      function applyPreset(id){
        var p = presetById(id);
        if (!p) return;
        curPreset = id;
        /* Never let a manually-entered background concentration or dose-basis
           acknowledgement leak from one product into another preset. */
        el("sameSolute").checked = false;
        el("sameSolute").dataset.user = "";
        el("sameSolute").dataset.auto = "";
        el("c0").value = "";
        el("c0u").value = "mgml";
        el("basisConfirm").checked = false;
        el("allowRounding").checked = false;
        el("drugName").value = p.drug;
        el("doseBasis").value = p.basis || p.drug;
        el("prepUse").value = p.use || "Injection or infusion";
        el("c1").value = p.c1[0]; el("c1u").value = p.c1[1];
        el("c2").value = p.c2[0]; el("c2u").value = p.c2[1];
        el("diluent").value = p.diluent || "Other diluent";
        el("otherDiluent").value = p.otherDiluent || "";
        bagTarget = "conc"; segSet("bagTarget", "data-t", "conc");
        el("bagMassWrap").classList.add("hide");
        if (p.mode === "bag"){
          bagMethod = "remove"; segSet("bagMethod", "data-m", "remove");
          el("bagVol").value = p.bag;
          segSet("bagChips", "data-v", String(p.bag));
          setMode("bag");
        } else {
          el("v2").value = p.v2;
          setMode("quick");
        }
        el("presetNote").innerHTML = "<b>" + esc(p.n) + ".</b> " + esc(p.note);
        el("presetNote").classList.remove("hide");
        clearFactorChips();
        refreshDiluentFields(true);
        setBagHint();
        setDilHint();
        renderPresets();
        presetSig = sigNow();
        calc();
      }
      function clearPreset(){
        curPreset = null; presetSig = null;
        el("presetNote").classList.add("hide");
        refreshDiluentFields(false);
        renderPresets();
      }

      /* ---------- mode ---------- */
      var MODEHELP = {
        quick:"You have a concentrated product and you need a set volume of a weaker one. Enter the strength you have, the strength you want and the total volume you need. It returns the millilitres of drug to draw up and the millilitres of diluent to add.",
        bag:"You are adding a drug to a fluid bag or bottle. It returns how many millilitres of the drug to add, whether to remove the same volume first, and the concentration you end up with.",
        check:"You have already mixed something, or you want to sanity check a recipe. Enter what you used and it tells you the concentration you actually made."
      };
      function setMode(m){
        mode = m;
        segSet("mode", "data-m", m);
        el("modeHelp").textContent = MODEHELP[m];
        el("bagBlock").classList.toggle("hide", m !== "bag");
        el("checkBlock").classList.toggle("hide", m !== "check");
        el("v2Wrap").classList.toggle("hide", m !== "quick");
        el("targetWrap").classList.toggle("hide", m === "check" || (m === "bag" && bagTarget === "mass"));
        el("factorChips").classList.toggle("hide", m !== "quick");
        el("inTitle").textContent = m === "check" ? "What you actually mixed" : "What you have and what you want";
        el("rTitle").textContent = m === "check" ? "What you made" : (m === "bag" ? "Add this much to the bag" : "Draw up this much drug");
        el("rMode").textContent = m === "quick" ? "Quick dilution" : (m === "bag" ? "Bag or bottle" : "Reverse check");
        if (m === "bag") el("bagMassWrap").classList.toggle("hide", bagTarget !== "mass");
      }

      /* ---------- calculation ---------- */
      function weightKg(){
        var w = num("wt");
        if (!isFinite(w) || w <= 0) return NaN;
        if (wtU === "lb") return w / LB;
        if (wtU === "g") return w / 1000;
        return w;
      }
      function calc(){
        var warns = [], R = null, blocked = null;

        if (curPreset && presetSig && sigNow() !== presetSig) clearPreset();
        refreshDiluentFields(false);

        var c1u = el("c1u").value, c2u = el("c2u").value, c0u = el("c0u").value;
        var c1v = num("c1"), c2v = num("c2"), c0v = num("c0");
        var c1 = toBase(c1v, c1u), c2 = toBase(c2v, c2u);
        var useC0 = el("sameSolute").checked;
        var c0 = useC0 ? toBase(c0v, c0u) : 0;
        var fam1 = famOf(c1u), fam2 = famOf(c2u), fam0 = famOf(c0u);
        var diluentChoice = el("diluent").value;
        var diluent = effectiveDiluent();
        var drug = el("drugName").value.trim();
        var patient = el("patientRef").value.trim();
        var prepUse = el("prepUse").value;
        var preset = presetById(curPreset);
        var bag = num("bagVol");
        var knownDiluent = backgroundSolute(diluentChoice, soluteOf(drug, preset));
        var knownSameSolute = knownDiluent && (fam1 === "mass" || fam1 === "meq" || fam1 === "mmol");

        el("c1Echo").textContent = echoFor(c1v, c1u);
        el("c2Echo").textContent = (mode === "check") ? "" : echoFor(c2v, c2u);
        el("c0Echo").textContent = useC0 ? echoFor(c0v, c0u) : "";
        ratioUI("c1Wrap", "c1", c1u === "ratio");
        ratioUI("c2Wrap", "c2", c2u === "ratio");
        ratioUI("c0Wrap", "c0", c0u === "ratio");

        var fam = fam1;
        var incomplete = el("c1").value.trim() === "" ||
          (mode === "quick" && (el("c2").value.trim() === "" || el("v2").value.trim() === "")) ||
          (mode === "bag" && (el("bagVol").value.trim() === "" || (bagTarget === "conc" ? el("c2").value.trim() === "" : el("bagMass").value.trim() === ""))) ||
          (mode === "check" && (el("ckV1").value.trim() === "" || el("ckDil").value.trim() === "")) ||
          (useC0 && el("c0").value.trim() === "") ||
          (diluentChoice === "Other diluent" && diluent === "");

        if (diluentChoice === "Other diluent" && !diluent){
          blocked = "Enter the exact diluent or product name for the label.";
        } else if (preset && !presetDiluentOK(preset, diluentChoice, diluent)){
          blocked = preset.n + " requires " + allowedDiluentText(preset) + " in this calculator. Select the required diluent or clear the preset and follow a validated local protocol.";
        } else if (knownSameSolute && !useC0){
          blocked = knownDiluent.label + ". Turn on “Diluent already contains the same substance” so the recipe is corrected before use.";
        } else if (!isFinite(c1) || c1 <= 0){
          blocked = "Enter the concentration of the product you are holding.";
        } else if (useC0 && (!isFinite(c0) || c0 < 0)){
          blocked = "Enter the concentration already present in the diluent.";
        } else if (useC0 && fam0 !== fam1){
          blocked = "Unit mismatch. The stock and the concentration already in the diluent must be the same kind of unit.";
        } else if (useC0 && mode !== "check" && c0 >= c1){
          blocked = "The diluent concentration must be lower than the stock concentration for this dilution.";
        } else if (mode === "check"){
          var kv1 = num("ckV1"), kdil = num("ckDil");
          if (!isFinite(kv1) || kv1 <= 0) blocked = "Enter how many millilitres of the stock you used.";
          else if (!isFinite(kdil) || kdil < 0) blocked = "Enter how many millilitres of diluent you added.";
          else R = calcCheck(c1, kv1, kdil, c0);
        } else if (mode === "bag"){
          var massU = el("bagMassU").value, massV = num("bagMass");
          var massFam = AMAP[massU] ? AMAP[massU].fam : null;
          var massBase = isFinite(massV) ? massV * (AMAP[massU] ? AMAP[massU].f : NaN) : NaN;
          if (!isFinite(bag) || bag <= 0) blocked = "Enter the volume actually in the bag or bottle.";
          else if (bagTarget === "mass"){
            if (!isFinite(massBase) || massBase <= 0) blocked = "Enter the total amount that should be in the finished bag.";
            else if (massFam !== fam1) blocked = "Unit mismatch. The stock is measured in " + FAMLAB[fam1] + " but the amount you want in the bag is in " + FAMLAB[massFam] + ". Convert one of them first.";
            else if (massBase <= c0 * bag) blocked = "The bag already contains " + fmtAmt(c0 * bag, fam) + " of this substance, so the requested total cannot be reached by adding more stock.";
            else R = calcBag({c1:c1, c0:c0, bag:bag, method:bagMethod, target:"mass", mass:massBase});
          } else {
            if (!isFinite(c2) || c2 <= 0) blocked = "Enter the concentration you want the bag to end up at.";
            else if (fam1 !== fam2) blocked = "Unit mismatch. You cannot mix " + FAMLAB[fam1] + " with " + FAMLAB[fam2] + ". Both concentrations must be in the same kind of unit.";
            else if (c2 <= c0) blocked = "The target must be higher than the concentration already in the diluent (" + fmtConc(c0, fam) + ").";
            else if (c2 >= c1) blocked = "The target (" + fmtConc(c2, fam) + ") is not weaker than the stock (" + fmtConc(c1, fam) + "). Adding this drug to fluid cannot reach it.";
            else R = calcBag({c1:c1, c0:c0, bag:bag, method:bagMethod, target:"conc", c2:c2});
          }
        } else {
          var v2 = num("v2");
          if (!isFinite(c2) || c2 <= 0) blocked = "Enter the concentration you want to end up with.";
          else if (fam1 !== fam2) blocked = "Unit mismatch. The stock is in " + FAMLAB[fam1] + " and the target is in " + FAMLAB[fam2] + ". Convert one of them so both are the same kind of unit.";
          else if (!isFinite(v2) || v2 <= 0) blocked = "Enter the total final volume you need.";
          else if (c2 <= c0) blocked = "The target is at or below the concentration already present in the diluent. No positive volume of this stock can produce that target.";
          else if (c2 > c1) blocked = "You cannot dilute up. The stock is " + fmtConc(c1, fam) + " and you have asked for " + fmtConc(c2, fam) + ", which is stronger. Check the units or the vial.";
          else R = calcQuick(c1, c2, v2, c0);
        }

        if (R && (!isFinite(R.v1) || R.v1 < -1e-10 || !isFinite(R.finalVol) || R.finalVol <= 0)){
          R = null; blocked = "Those values do not produce a valid dilution. Recheck the concentrations and units.";
        }
        if (R && mode === "bag" && bagMethod === "remove" && R.v1 > bag + 1e-10){
          R = null; blocked = "This recipe needs more stock than the bag contains, so the remove-first method is impossible. Increase the final volume, use a stronger stock or change the target.";
        }

        if (!R){
          S = {blocked:blocked, warns:[], R:null};
          setActions(false);
          el("doseHint").textContent = "Enter a valid dilution above, then the weight and dose, to see the volume to give.";
          root.querySelector("[id^=\"resultCard-\"]").classList.toggle("blocked", !incomplete);
          el("rTitle").textContent = incomplete ? "Ready for your values" : "Check the inputs";
          el("rBody").innerHTML = incomplete
            ? '<div class="empty">Enter the stock concentration, target and volume, or choose a reviewed preset to begin.</div>'
            : '<div class="warn red" style="margin-top:0"><b>Cannot calculate. </b>' + esc(blocked || "Recheck the values.") + "</div>";
          if (!incomplete) announce(blocked || "Check the inputs.");
          writeHashSoon();
          return;
        }
        root.querySelector("[id^=\"resultCard-\"]").classList.remove("blocked");
        setMode(mode);

        var minV = minMeasure(), lowV = Math.max(0.1, minV * 2);
        var unmeasurable = (mode !== "check") && R.v1 > 0 && R.v1 < minV;
        var directV1 = R.v1;
        var drawForFactor = function(k){
          var cInt = c0 + (c1 - c0) / k;
          if (mode === "bag" && bagMethod === "add"){
            if (bagTarget === "mass") return (R.total - c0 * bag) / cInt;
            if (cInt <= c2) return NaN;
            return (c2 - c0) * bag / (cInt - c2);
          }
          return directV1 * k;
        };
        /* Every staged 1-in-10 step makes 10 mL. Never prescribe a final draw larger
           than the intermediate volume the instructions actually prepare. */
        var contextMaxDraw = (mode === "quick") ? R.finalVol : (mode === "bag" && bagMethod === "remove") ? bag : Infinity;
        var maxDraw = Math.min(10, contextMaxDraw);
        var plan = unmeasurable ? serialPlan(directV1, lowV, 4, drawForFactor, maxDraw) : null;
        if (plan){
          plan.intermediateConc = c0 + (c1 - c0) / plan.k;
          plan.directV1 = directV1;
          plan.deliveredNeatEquivalent = plan.draw / plan.k;
          plan.stockWithdrawn = 1;
          if (mode === "bag" && bagMethod === "add"){
            R.finalVol = bag + plan.draw;
            R.total = c0 * bag + plan.intermediateConc * plan.draw;
            R.finalConc = R.total / R.finalVol;
          }
          R.factor = R.finalVol / plan.deliveredNeatEquivalent;
        }

        /* A syringe capacity is not a graduation. Check the actionable draw against
           the actual increment selected by the clinic, without silently rounding it. */
        var increment = measureIncrement();
        var prepDraw = plan ? plan.draw : R.v1;
        var measurement = null;
        if (mode !== "check" && prepDraw > 0){
          var nearest = Math.round(prepDraw / increment) * increment;
          var delta = Math.abs(nearest - prepDraw);
          if (delta > Math.max(1e-12, increment * 1e-7)){
            var sourceConcForMeasure = plan ? plan.intermediateConc : c1;
            var achievedVol, achievedTotal;
            if (mode === "bag" && bagMethod === "add"){
              achievedVol = bag + nearest;
              achievedTotal = c0 * bag + sourceConcForMeasure * nearest;
            } else {
              achievedVol = mode === "bag" ? bag : R.finalVol;
              achievedTotal = sourceConcForMeasure * nearest + c0 * (achievedVol - nearest);
            }
            measurement = {increment:increment, exact:prepDraw, nearest:nearest, relative:nearest > 0 ? delta / prepDraw : Infinity,
              requestedVol:R.finalVol, requestedTotal:R.total, requestedConc:R.finalConc,
              achievedVol:achievedVol, achievedTotal:achievedTotal, achievedConc:achievedTotal / achievedVol};
            measurement.permittable = isFinite(measurement.achievedConc) && nearest > 0 && nearest <= achievedVol + 1e-10 && measurement.relative <= 0.001;
            measurement.accepted = measurement.permittable && el("allowRounding").checked;
            measurement.blocked = !measurement.accepted;
            if (measurement.blocked) el("measurementSafety").open = true;
            if (measurement.accepted){
              prepDraw = nearest;
              R.finalVol = achievedVol;
              R.total = achievedTotal;
              R.finalConc = measurement.achievedConc;
              if (plan){
                plan.draw = nearest;
                plan.deliveredNeatEquivalent = nearest / plan.k;
                plan.measurementAdjusted = true;
                R.factor = R.finalVol / plan.deliveredNeatEquivalent;
              } else {
                R.v1 = nearest;
                R.diluent = (mode === "bag" && bagMethod === "add") ? bag : achievedVol - nearest;
                R.stockAmount = c1 * nearest;
                R.diluentAmount = c0 * R.diluent;
                R.factor = R.finalVol / nearest;
              }
            }
          }
        }

        /* ----- warnings ----- */
        if (mode !== "check" && Math.abs(R.factor - 1) < 1e-9){
          warns.push({lvl:"info", t:"The target is the same as the stock. No diluent is needed: use the product neat."});
        }
        if (mode === "check" && R.factor < 1.5 && R.factor > 1.0000001){
          warns.push({lvl:"amber", t:"You used more stock than diluent, so this is only a <b>1 in " + fmt(R.factor) + "</b> volume dilution. Check that the two volumes were not reversed."});
        }
        if (mode !== "check"){
          if (unmeasurable){
            warns.push({lvl:"red", t:"The direct stock volume is <b>" + vol(directV1) + " mL</b>, below your " + fmt(minV) +
              " mL measurement threshold. " + (plan ? "Do not estimate it: use the staged serial dilution below." : "No feasible serial plan fits this final volume. Make a larger batch or obtain a more dilute stock.")});
          } else if (R.v1 > 0 && R.v1 < lowV){
            warns.push({lvl:"amber", t:"The stock volume is <b>" + vol(R.v1) + " mL</b>, close to your low-volume threshold. Use an appropriately graduated, validated low-volume device and account for dead space."});
          }
          if (prepDraw > 60){
            warns.push({lvl:"amber", t:"The preparation requires <b>" + fmt(prepDraw) + " mL</b> in the draw/add step. Use a graduated container or measured aliquots with an independent count."});
          }
        }
        var actualBagAdd = plan ? plan.draw : R.v1;
        if (mode === "bag" && bagMethod === "add" && actualBagAdd / R.finalVol > 0.02){
          warns.push({lvl:"amber", t:"You are adding <b>" + fmt(actualBagAdd) + " mL</b> on top of a " + fmt(bag) + " mL bag, so the finished volume is <b>" + fmt(R.finalVol) + " mL</b>. If the bag must stay at its labelled volume, switch to remove-first."});
        }
        if (mode === "bag"){
          warns.push({lvl:"info", t:"Use the actual or protocol-approved starting bag volume; commercial bags may contain overfill. Mix thoroughly according to clinic policy before connecting the patient."});
        }

        if (useC0 && c0 > 0){
          warns.push({lvl:"ok", t:"Background-solute correction is applied: " + esc(diluent) + " already contains <b>" + fmtConc(c0, fam) + "</b> of the same substance."});
        }
        if (diluentChoice === "Sterile water for injection" && prepUse === "Injection or infusion"){
          warns.push({lvl:"amber", t:"Sterile water is hypotonic. Confirm the final preparation's tonicity and route before parenteral administration."});
        }
        if (diluentChoice === "5% dextrose" || diluentChoice === "Lactated Ringer's (Hartmann's)"){
          warns.push({lvl:"info", t:"Check product-specific compatibility and stability with " + esc(diluent) + " before mixing."});
        }
        if (diluentChoice === "Cooled boiled water"){
          warns.push({lvl:prepUse === "Oral administration" || prepUse === "Topical use or wound lavage" ? "amber" : "red",
            t:"Cooled boiled water is not sterile. It is unsuitable for injectable preparations and should only be used when the product and route explicitly allow it."});
        }
        if (fam === "meq" || fam === "mmol"){
          warns.push({lvl:"amber", t:"For concentrated electrolytes, independently verify the final concentration and delivered rate against the patient's weight and clinic protocol before administration."});
        }
        var syrVol = plan ? plan.draw : R.v1;
        var syr = syringeFor(syrVol);
        if (mode !== "check" && syr && (!unmeasurable || plan)){
          warns.push({lvl:"info", t:"The draw/add step fits within a <b>" + syr + " mL syringe</b>. That is a capacity check only: verify the selected device's graduations, validated minimum and dead space."});
        }
        if (measurement){
          var exactText = String(Number(measurement.exact.toPrecision(8)));
          var achievedText = mode === "bag" && bagTarget === "mass" ? fmtAmt(measurement.achievedTotal, fam) + " total" : fmtConcPrecise(measurement.achievedConc, fam);
          if (measurement.accepted){
            warns.push({lvl:"amber", t:"Nearest-mark adjustment accepted: the exact draw was <b>" + exactText + " mL</b>; this recipe now uses <b>" + fmt(measurement.nearest) + " mL</b> on the selected " + fmt(increment) + " mL increment and labels the achieved result as <b>" + achievedText + "</b>. Independently verify that deviation against the product-specific protocol."});
          } else {
            warns.push({lvl:"red", t:"The exact calculated draw (<b>" + exactText + " mL</b>) falls between the selected <b>" + fmt(increment) + " mL</b> device increments. The nearest mark is " + fmt(measurement.nearest) + " mL, which would produce " + achievedText + ". The calculator has not rounded the recipe. " + (measurement.permittable ? "If that result is acceptable under the product-specific protocol, independently check it and select the nearest-mark adjustment in the open Measurement safety setting; otherwise scale the batch or choose a finer validated device." : "The deviation exceeds 0.1%; scale the batch or choose a validated finer device before preparing it.")});
          }
        }
        if (preset){
          warns.push({lvl:preset.lvl || "info", key:"preset", t:"<b>" + esc(preset.n) + ".</b> " + esc(preset.note)});
          warns.push({lvl:"info", t:"Read the concentration from the vial in your hand. Product strengths vary by market and manufacturer."});
        }
        if (plan){
          warns.push({lvl:"amber", t:"The serial workflow withdraws <b>1 mL of neat stock</b> to make Stage 1, while the final preparation contains the equivalent of <b>" + fmt(plan.deliveredNeatEquivalent) + " mL</b> of neat stock. Record and discard all remaining intermediate solution according to controlled-drug, hazardous-drug and clinic policy."});
        }
        warns.push({lvl:"info", t:"Label the container now: patient/case where applicable, drug, concentration, diluent, route/use, date, time and initials. Use an independent check for high-risk preparations."});

        /* ----- dose check ----- */
        var dose = null;
        var wkg = weightKg(), dv = num("doseVal"), du = el("doseU").value;
        var dfam = DMAP[du] ? DMAP[du].fam : null;
        var doseBasis = el("doseBasis").value.trim();
        var basisConfirmed = el("basisConfirm").checked;
        if (isFinite(wkg) && wkg > 0 && isFinite(dv) && dv > 0){
          if (dfam !== fam){
            el("doseHint").innerHTML = "The dose and concentration use different kinds of units. Pick a dose in " + FAMLAB[fam] + ".";
            warns.push({lvl:"amber", t:"Dose check not calculated: the dose unit and concentration unit families do not match."});
          } else if (!doseBasis){
            el("doseHint").innerHTML = "Enter the exact basis used by both the prescribed dose and the concentration, such as <b>elemental calcium</b>, <b>calcium gluconate salt</b> or <b>iodine</b>.";
            warns.push({lvl:"amber", t:"Dose check not calculated: the active-moiety basis has not been entered."});
          } else if (preset && preset.basis && doseBasis.toLowerCase() !== preset.basis.toLowerCase()){
            el("doseHint").innerHTML = "This reviewed preset's concentration is fixed to a <b>" + esc(preset.basis) + "</b> basis. Clear the preset and enter a correctly converted concentration before using a different basis.";
            warns.push({lvl:"red", t:"Dose check not calculated: “" + esc(doseBasis) + "” does not match this preset's “" + esc(preset.basis) + "” concentration basis."});
          } else if (!basisConfirmed){
            el("doseHint").innerHTML = "Confirm that both values use the same basis (<b>" + esc(doseBasis) + "</b>) before a dose volume is shown.";
            warns.push({lvl:"amber", t:"Dose check not calculated until the prescribed dose and concentration basis (“" + esc(doseBasis) + "”) are independently checked."});
          } else {
            var amt = dv * DMAP[du].f * wkg;
            var dvol = amt / R.finalConc, volNeat = amt / c1;
            var exactDoses = R.finalVol / dvol;
            if (dvol < minV){
              el("doseHint").innerHTML = "Calculated volume: <b>" + vol(dvol) + " mL</b> on a <b>" + esc(doseBasis) + "</b> basis. <b>Do not administer from this calculation:</b> it is below the selected measurement threshold.";
              warns.push({lvl:"red", t:"Even after dilution, the dose volume is <b>" + vol(dvol) + " mL</b>, below your " + fmt(minV) + " mL threshold. Dilute further or use a validated lower-volume device."});
            } else if (dvol > R.finalVol + 1e-10){
              el("doseHint").innerHTML = "Calculated volume: <b>" + vol(dvol) + " mL</b> on a <b>" + esc(doseBasis) + "</b> basis. <b>Do not administer from this batch:</b> it contains only " + fmt(R.finalVol) + " mL.";
              warns.push({lvl:"red", t:"The calculated dose volume is <b>" + vol(dvol) + " mL</b>, but this preparation contains only " + fmt(R.finalVol) + " mL. Prepare a larger batch and independently recheck the dose."});
            } else {
              dose = {amt:amt, vol:dvol, volNeat:volNeat, wkg:wkg, doses:exactDoses, wholeDoses:Math.floor(exactDoses + 1e-10), rate:dv, rateU:DMAP[du].lab, basis:doseBasis};
              el("doseHint").innerHTML = "A <b>" + fmt(wkg) + " kg</b> patient at <b>" + fmt(dv) + " " + DMAP[du].lab +
                "</b> on a <b>" + esc(doseBasis) + "</b> basis needs <b>" + fmtAmt(amt, fam) + "</b>, equal to <b>" + vol(dvol) + " mL</b> of the diluted solution.";
              if (volNeat < minV) warns.push({lvl:"ok", t:"The neat dose volume was <b>" + vol(volNeat) + " mL</b>; the diluted dose is <b>" + vol(dvol) + " mL</b>."});
            }
          }
        } else {
          el("doseHint").textContent = "Enter body weight and a dose per kg to see the administration volume.";
        }

        var outputBlockedReason = null;
        if (unmeasurable && !plan) outputBlockedReason = "No feasible measurable preparation fits the requested final volume.";
        else if (measurement && measurement.blocked) outputBlockedReason = measurement.permittable
          ? "The calculated draw falls between device marks. Independently accept the displayed nearest-mark result or change the batch/device before preparing it."
          : "The calculated draw cannot be adjusted to the selected device increment within 0.1% arithmetic deviation. Scale the batch or choose a validated finer device.";
        else if (!drug) outputBlockedReason = "Enter the exact drug or product name before generating a label or exporting the record.";
        else if (diluentChoice === "Cooled boiled water" && prepUse === "Injection or infusion") outputBlockedReason = "Cooled boiled water is not sterile and cannot be used for an injectable preparation.";
        else if (preset && prepUse !== (preset.use || "Injection or infusion")) outputBlockedReason = preset.n + " is restricted to “" + (preset.use || "Injection or infusion") + "” while its reviewed preset remains active. Clear the preset before using a different validated workflow.";

        S = {mode:mode, c1:c1, c2:c2, c0:c0, c1v:c1v, c1u:c1u, c2v:c2v, c2u:c2u, c0v:c0v, c0u:c0u,
             fam:fam, R:R, warns:warns, diluent:diluent, diluentChoice:diluentChoice, drug:drug, patient:patient,
             prepUse:prepUse, dose:dose, doseBasis:doseBasis, basisConfirmed:basisConfirmed, bagMethod:bagMethod, bagTarget:bagTarget, bag:bag, minMeasure:minV, measureIncrement:increment, measurement:measurement, allowRounding:el("allowRounding").checked,
             preset:curPreset, unmeasurable:unmeasurable, plan:plan, blocked:null, useC0:useC0, outputBlockedReason:outputBlockedReason};
        setActions(!outputBlockedReason);
        render();
        writeHashSoon();
      }

      /* ---------- steps, built once and shared by screen, record and print ---------- */
      function buildSteps(){
        var R = S.R, fam = S.fam, blocks = [];
        if (S.mode === "check"){
          var checked = [
            "Took <b>" + fmt(R.v1) + " mL</b> of " + fmtConc(S.c1, fam) + " stock.",
            "Added <b>" + fmt(R.diluent) + " mL</b> of " + esc(S.diluent) + (S.useC0 ? " at " + fmtConc(S.c0, fam) + " of the same substance." : "."),
            "Result: <b>" + fmt(R.finalVol) + " mL</b> at <b>" + fmtConc(R.finalConc, fam) + "</b>, containing " + fmtAmt(R.total, fam) + " in total."
          ];
          blocks.push({tag:"What you mixed", items:checked});
          return blocks;
        }
        if (S.unmeasurable && !S.plan){
          blocks.push({tag:"Do not prepare this recipe directly", items:[
            "The calculated stock volume is <b>" + vol(R.v1) + " mL</b>, below your " + fmt(S.minMeasure) + " mL threshold.",
            "A staged 1-in-10 intermediate cannot produce the requested concentration within this final volume. Increase the batch size or obtain a suitable pre-diluted product, then recalculate.",
            "Do not estimate the volume and do not use the direct draw shown in the working."
          ]});
          return blocks;
        }
        if (S.measurement && S.measurement.blocked){
          blocks.push({tag:"Stop before preparing", items:[
            "The exact draw of <b>" + String(Number(S.measurement.exact.toPrecision(8))) + " mL</b> does not land on the selected <b>" + fmt(S.measureIncrement) + " mL</b> device increment.",
            "Do not round it. Scale the final volume or choose a validated finer device, then recalculate."
          ]});
          return blocks;
        }
        if (S.outputBlockedReason){
          blocks.push({tag:"Stop before preparing", items:[esc(S.outputBlockedReason)]});
          return blocks;
        }
        if (S.unmeasurable && S.plan){
          var serial = [], source = S.drug ? esc(S.drug) : "the stock";
          for (var stage = 1; stage <= S.plan.stages; stage++){
            var stageConc = S.c0 + (S.c1 - S.c0) / Math.pow(10, stage);
            serial.push("<b>Stage " + stage + ".</b> Put <b>1 mL</b> of " + (stage === 1 ? source : "the Stage " + (stage - 1) + " intermediate") +
              " into a new labelled container, then add <b>9 mL</b> of " + esc(S.diluent) + ". Mix. This stage is <b>" + fmtConc(stageConc, fam) + "</b>.");
          }
          if (S.mode === "bag" && S.bagMethod === "remove"){
            serial.push("Remove and discard <b>" + fmt(S.plan.draw) + " mL</b> from the " + fmt(S.bag) + " mL bag, then add <b>" + fmt(S.plan.draw) + " mL</b> from the final intermediate.");
            serial.push("The bag remains <b>" + fmt(R.finalVol) + " mL</b>. Mix thoroughly and label it <b>" + fmtConc(R.finalConc, fam) + "</b>.");
          } else if (S.mode === "bag"){
            serial.push("Add <b>" + fmt(S.plan.draw) + " mL</b> from the final intermediate to the " + fmt(S.bag) + " mL bag without removing anything.");
            serial.push("The finished volume is <b>" + fmt(R.finalVol) + " mL</b> at <b>" + fmtConc(R.finalConc, fam) + "</b>. Write the new volume on the bag and mix thoroughly.");
          } else {
            serial.push("From the final intermediate, draw <b>" + fmt(S.plan.draw) + " mL</b>, then add <b>" + fmt(R.finalVol - S.plan.draw) + " mL</b> of " + esc(S.diluent) + " to reach <b>" + fmt(R.finalVol) + " mL</b>.");
          }
          serial.push("Keep every intermediate separate and labelled. Discard all intermediates immediately after the final preparation is complete.");
          blocks.push({tag:"Staged serial dilution", items:serial});
          return blocks;
        }
        if (Math.abs(R.diluent) < 1e-10 && S.mode === "quick"){
          blocks.push({tag:"No dilution required", items:[
            "Use <b>" + fmt(R.v1) + " mL</b> of " + (S.drug ? esc(S.drug) : "the stock") + " neat at <b>" + fmtConc(S.c1, fam) + "</b>.",
            "Do not add diluent. Label the syringe or container with the stock concentration and intended use."
          ]});
          return blocks;
        }
        if (S.mode === "bag"){
          var items = [];
          if (S.bagMethod === "remove"){
            items.push("Remove and discard <b>" + fmt(R.v1) + " mL</b> from the " + fmt(R.finalVol) + " mL bag, leaving <b>" + fmt(R.diluent) + " mL</b>.");
            items.push("Add <b>" + fmt(R.v1) + " mL</b> of " + (S.drug ? esc(S.drug) : "the drug") + " at " + fmtConc(S.c1, fam) + " to the remaining " + fmt(R.diluent) + " mL.");
          } else {
            items.push("Add <b>" + fmt(R.v1) + " mL</b> of " + (S.drug ? esc(S.drug) : "the drug") + " at " + fmtConc(S.c1, fam) + " to the " + fmt(R.finalVol - R.v1) + " mL bag without removing anything.");
            items.push("The bag now holds <b>" + fmt(R.finalVol) + " mL</b>, not " + fmt(R.finalVol - R.v1) + " mL. Write the new volume on the bag.");
          }
          items.push("Invert the bag several times, then label it: " + fmtAmt(R.total, fam) + " in " + fmt(R.finalVol) + " mL = <b>" + fmtConc(R.finalConc, fam) + "</b>.");
          blocks.push({tag:"How to make it", items:items});
          return blocks;
        }
        blocks.push({tag:"How to make it", items:[
          "Draw up <b>" + fmt(R.v1) + " mL</b> of " + (S.drug ? esc(S.drug) : "the stock") + " at " + fmtConc(S.c1, fam) + ".",
          "Add <b>" + fmt(R.diluent) + " mL</b> of " + esc(S.diluent) + " to make <b>" + fmt(R.finalVol) + " mL</b> in total.",
          "Mix, then label: <b>" + fmtConc(R.finalConc, fam) + "</b>, " + fmtAmt(R.total, fam) + " in " + fmt(R.finalVol) + " mL."
        ]});
        if (!S.useC0){
          blocks.push({tag:"Alternative, instead of the steps above", alt:true, letters:true, items:[
            "Remove <b>" + fmt(R.v1) + " mL</b> from a " + fmt(R.finalVol) + " mL container of " + esc(S.diluent) + " and discard it, leaving <b>" + fmt(R.diluent) + " mL</b>.",
            "Add <b>" + fmt(R.v1) + " mL</b> of the drug to that remaining " + fmt(R.diluent) + " mL. The total stays <b>" + fmt(R.finalVol) + " mL</b>."
          ]});
        }
        return blocks;
      }

      /* ---------- render ---------- */
      function render(){
        var R = S.R, fam = S.fam, h = "";
        var isCheck = S.mode === "check";
        var activePreset = presetById(S.preset), noInjection = activePreset && activePreset.noInjection;
        var head, headU;
        if (isCheck){
          var c = R.finalConc;
          if (fam === "mass" && c > 0 && c < 0.1){ head = fmt(c * 1000); headU = "mcg/mL"; }
          else { head = fmt(c); headU = BASELAB[fam] + "/mL"; }
        } else if (S.plan){
          head = vol(S.plan.draw); headU = "mL from final intermediate";
        } else {
          head = vol(R.v1); headU = "mL";
        }

        var reds = S.warns.filter(function(w){ return w.lvl === "red"; });
        var rest = S.warns.filter(function(w){ return w.lvl !== "red"; });
        if (reds.length) h += warnHTML(reds, true);

        h += '<div class="bignum' + ((S.unmeasurable && !S.plan) || (S.measurement && S.measurement.blocked) ? " struck" : "") + '"><div class="n">' + head + '</div><div class="u">' + headU + "</div></div>";

        if (isCheck){
          h += '<div class="subline">' + (S.drug ? "<b>" + esc(S.drug) + "</b>, " : "") + "made from " + fmt(R.v1) + " mL of " +
               fmtConc(S.c1, fam) + " plus " + fmt(R.diluent) + " mL of " + esc(S.diluent) +
               ". Final volume <b>" + fmt(R.finalVol) + " mL</b>; stock was <b>1 in " + fmt(R.factor) + "</b> of the mixed volume.</div>";
        } else if (S.plan){
          h += '<div class="subline">from the final 1-in-' + S.plan.k + ' intermediate. A direct, no-serial recipe would require <b>' + vol(S.plan.directV1) +
               ' mL</b>, below your threshold, so follow every staged step below.</div>';
        } else if (S.unmeasurable){
          h += '<div class="subline">is the calculated direct stock volume, but it is <b>too small to measure</b> and no feasible staged plan fits this preparation. Do not estimate it.</div>';
        } else {
          h += '<div class="subline">of ' + (S.drug ? "<b>" + esc(S.drug) + "</b> at " : "your stock at ") + fmtConc(S.c1, fam) +
               ", giving <b>" + fmt(R.finalVol) + " mL</b> at <b>" + fmtConc(R.finalConc, fam) + "</b>.</div>";
        }

        buildSteps().forEach(function(b){
          h += '<div class="steps' + (b.alt ? " alt" : "") + '"><div class="stag">' + esc(b.tag) + "</div><ol" + (b.letters ? ' class="alpha"' : "") + ">";
          b.items.forEach(function(i){ h += "<li>" + i + "</li>"; });
          h += "</ol></div>";
        });

        /* tiles */
        if (!S.outputBlockedReason || isCheck){
        var tile1t, tile1v, tile1n;
        if (S.mode === "bag" && S.bagMethod === "remove"){
          tile1t = "Remove from bag";
          tile1v = (S.unmeasurable && S.plan) ? fmt(S.plan.draw) : fmt(R.v1);
          tile1n = (S.unmeasurable && S.plan) ? ("then add the same volume of the 1 in " + S.plan.k + " intermediate") : "then add the same volume of drug";
        }
        else if (S.mode === "bag"){ tile1t = "Remove from bag"; tile1v = "0"; tile1n = "add on top; finished volume increases"; }
        else if (S.plan){ tile1t = "Final-step diluent"; tile1v = fmt(R.finalVol - S.plan.draw); tile1n = esc(S.diluent); }
        else { tile1t = isCheck ? "Diluent used" : "Diluent to add"; tile1v = fmt(R.diluent); tile1n = R.diluent > 1e-10 ? esc(S.diluent) : "none; use neat"; }
        h += '<div class="pairs">';
        h += '<div class="pair"><div class="pt">' + tile1t + '</div><div class="pv">' + tile1v + ' <small>mL</small></div><div class="pn">' + tile1n + "</div></div>";
        h += '<div class="pair"><div class="pt">Final volume</div><div class="pv">' + fmt(R.finalVol) + ' <small>mL</small></div><div class="pn">including the drug</div></div>';
        h += '<div class="pair"><div class="pt">Final concentration</div><div class="pv">' + fmtConc(R.finalConc, fam).replace(/ (mg|mcg|mEq|mmol|IU)\/mL$/, ' <small>$1/mL</small>') +
             '</div><div class="pn">' + concAlt(R.finalConc, fam) + (S.doseBasis ? " · basis: " + esc(S.doseBasis) : "") + "</div></div>";
        h += '<div class="pair"><div class="pt">Total substance in the container</div><div class="pv">' + fmtAmt(R.total, fam) +
             '</div><div class="pn">neat-stock-equivalent fraction: 1 in ' + fmt(R.factor) + "</div></div>";
        h += "</div>";

        if (S.dose){
          h += '<div class="titr"><div class="ttag">Dose check</div><div class="tsub">A <b>' + fmt(S.dose.wkg) + " kg</b> patient at " +
               fmt(S.dose.rate) + " " + S.dose.rateU + " on a <b>" + esc(S.dose.basis) + "</b> basis needs <b>" + fmtAmt(S.dose.amt, fam) + "</b>.</div>" +
               '<table><caption class="sr">Dose volume comparison</caption><thead><tr><th scope="col">Give</th><th scope="col">Volume</th><th scope="col">Whole doses</th></tr></thead><tbody>' +
               "<tr><td>Diluted solution</td><td><b>" + vol(S.dose.vol) + " mL</b></td><td>" + fmt(S.dose.wholeDoses) + "</td></tr>" +
               (S.dose.volNeat >= S.minMeasure ? "<tr><td>Neat product, for comparison only</td><td>" + vol(S.dose.volNeat) + " mL</td><td>&mdash;</td></tr>" : "<tr><td>Neat product</td><td>Below threshold — do not measure</td><td>&mdash;</td></tr>") + "</tbody></table></div>";
        }
        }

        if (S.outputBlockedReason){
          h += '<div class="dwarn red"><b>No preparation label or export generated.</b> ' + esc(S.outputBlockedReason) + '</div>';
        } else {
          h += '<div class="verifybar"><b>Independent read-back:</b> vial concentration → calculated draw → final concentration → label. High-risk preparations should be checked independently before administration.</div>';
          h += '<div class="label"><div class="ltag">Syringe or bag label</div><div class="lmain">' +
               (noInjection ? "NOT FOR INJECTION — " : "") + (S.drug ? esc(S.drug) + " " : "") + fmtConc(R.finalConc, fam) + '</div><div class="lsub">' +
               (S.patient ? "Patient/case: " + esc(S.patient) + " &nbsp;|&nbsp; " : "") + fmtAmt(R.total, fam) + " in " + fmt(R.finalVol) + " mL" +
               (R.diluent > 1e-10 || S.mode === "bag" ? " &nbsp;|&nbsp; " + esc(S.diluent) : " &nbsp;|&nbsp; neat") +
               (S.doseBasis ? " &nbsp;|&nbsp; basis: " + esc(S.doseBasis) : "") +
               " &nbsp;|&nbsp; " + esc(S.prepUse) + ' &nbsp;|&nbsp; <span id="labelStamp"></span> &nbsp;|&nbsp; initials <span class="rule"></span><br>Use immediately; discard remainder unless a validated beyond-use time applies.</div></div>';
        }

        h += '<div class="working"><div class="wt">Working, so you can check it by hand</div>' + workingHTML() + "</div>";
        h += warnHTML(rest, true);
        el("rBody").innerHTML = h;
        var st = el("labelStamp");
        if (st) st.textContent = stamp();

        announce(shortSummary());
      }
      function shortSummary(){
        var R = S.R, fam = S.fam;
        if (!R) return S.blocked || "";
        if (S.outputBlockedReason) return "Stop. " + S.outputBlockedReason;
        if (S.mode === "check") return "Result: " + fmtConc(R.finalConc, fam) + " in " + fmt(R.finalVol) + " mL.";
        if (S.plan) return "Use " + fmt(S.plan.draw) + " millilitres from the final 1-in-" + S.plan.k + " intermediate; final " + fmt(R.finalVol) + " millilitres at " + fmtConc(R.finalConc, fam) + ".";
        if (S.unmeasurable) return "The direct volume of " + vol(R.v1) + " millilitres is below the measurement threshold and no feasible serial plan fits. Make a larger batch.";
        if (S.mode === "bag") return "Add " + fmt(R.v1) + " millilitres to the bag. Final " + fmt(R.finalVol) + " millilitres at " + fmtConc(R.finalConc, fam) + ".";
        if (R.diluent <= 1e-10) return "Use " + fmt(R.v1) + " millilitres neat at " + fmtConc(R.finalConc, fam) + ".";
        return "Draw up " + fmt(R.v1) + " millilitres, add " + fmt(R.diluent) + " millilitres of " + S.diluent + ", giving " + fmt(R.finalVol) + " millilitres at " + fmtConc(R.finalConc, fam) + ".";
      }
      function announce(msg){
        clearTimeout(srTimer);
        srTimer = setTimeout(function(){ el("srSummary").textContent = msg; }, 450);
      }
      function concAlt(base, fam){
        if (fam !== "mass") return "&nbsp;";
        var u = (S.mode === "check") ? null : S.c2u;
        if (u === "ratio" && base > 0) return "1:" + fmtRatio(1000 / base);
        var pct = fmt(base / 10) + "% w/v";
        if (base < 0.1) return pct + "  ·  " + fmt(base) + " mg/mL";
        return pct;
      }

      /* Mass-balance working. C0 is the concentration already in the diluent. */
      function workingHTML(){
        var R = S.R, fam = S.fam, w = "";
        var formulaV1 = S.measurement && S.measurement.accepted && !S.plan ? S.measurement.exact : R.v1;
        var targetTotal = S.measurement && S.measurement.accepted ? S.measurement.requestedTotal : R.total;
        var c1s = fmt(S.c1), c0s = fmt(S.c0), c2s = fmt(R.finalConc), v2s = fmt(R.finalVol), v1s = fmt(formulaV1), dils = fmt(R.diluent);
        var U = BASELAB[fam];
        var sourceC = S.plan ? S.plan.intermediateConc : S.c1;
        var sourceV = S.plan ? S.plan.draw : R.v1;
        var carrierV = S.mode === "bag" ? (S.bagMethod === "add" ? S.bag : S.bag - sourceV)
                                            : (S.mode === "check" ? R.diluent : R.finalVol - sourceV);
        if (S.mode === "check"){
          w += '<div class="step"><b>Final volume</b> = stock + diluent = ' + v1s + " + " + dils + " = " + fmt(R.finalVol) + " mL</div>";
          w += '<div class="step"><b>Total substance</b> = (C1 × stock) + (C0 × diluent) = (' + c1s + " × " + v1s + ") + (" + c0s + " × " + dils + ") = " + fmt(R.total) + " " + U + "</div>";
          w += '<div class="step"><b>Final concentration</b> = total / final volume = ' + fmt(R.total) + " / " + v2s + " = " + c2s + " " + U + "/mL</div>";
        } else if (S.mode === "bag" && S.bagTarget === "mass"){
          if (S.bagMethod === "remove"){
            w += '<div class="step"><b>Direct no-serial alternative</b> = (target total − C0 × bag) / (C1 − C0) = (' + fmt(targetTotal) + " − " + c0s + " × " + fmt(S.bag) + ") / (" + c1s + " − " + c0s + ") = " + v1s + " mL</div>";
          } else {
            w += '<div class="step"><b>Direct no-serial alternative</b> = (target total − C0 × bag) / C1 = (' + fmt(targetTotal) + " − " + c0s + " × " + fmt(S.bag) + ") / " + c1s + " = " + v1s + " mL</div>";
          }
        } else {
          if (S.mode === "bag" && S.bagMethod === "add"){
            w += '<div class="step"><b>Direct no-serial alternative</b> = (C2 − C0) × bag / (C1 − C2) = (' + fmt(S.c2) + " − " + c0s + ") × " + fmt(S.bag) + " / (" + c1s + " − " + fmt(S.c2) + ") = " + v1s + " mL</div>";
          } else {
            var fixedVol = S.mode === "bag" ? S.bag : (S.R.finalVol);
            w += '<div class="step"><b>Direct no-serial alternative</b> = (C2 − C0) × V2 / (C1 − C0) = (' + fmt(S.c2) + " − " + c0s + ") × " + fmt(fixedVol) + " / (" + c1s + " − " + c0s + ") = " + v1s + " mL</div>";
          }
        }
        if (S.unmeasurable && S.plan){
          w += '<div class="step"><b>Final intermediate</b> = C0 + (C1 − C0) / ' + S.plan.k + " = " + fmt(S.plan.intermediateConc) + " " + U + "/mL after " + S.plan.stages + " staged 1-in-10 dilution" + (S.plan.stages > 1 ? "s" : "") + "</div>";
        }
        if (S.measurement && S.measurement.accepted){
          w += '<div class="step"><b>Accepted device-mark adjustment</b> = ' + String(Number(S.measurement.exact.toPrecision(8))) + " mL calculated → " + fmt(S.measurement.nearest) + " mL used; achieved result = " + (S.mode === "bag" && S.bagTarget === "mass" ? fmtAmt(S.measurement.achievedTotal, fam) + " total" : fmtConcPrecise(S.measurement.achievedConc, fam)) + "</div>";
        }
        if (S.mode !== "check"){
          w += '<div class="step"><b>Preparation check</b> = (' + fmt(sourceC) + " × " + fmt(sourceV) + ") + (" + c0s + " × " + fmt(carrierV) + ") = " + fmt(R.total) + " " + U + " in " + v2s + " mL = " + c2s + " " + U + "/mL</div>";
        }
        if (S.dose){
          w += '<div class="step"><b>Dose volume (' + esc(S.dose.basis) + ' basis)</b> = ' + fmt(S.dose.amt) + " " + U + " / " + c2s + " " + U + "/mL = " + fmt(S.dose.vol) + " mL</div>";
        }
        return w;
      }
      function stamp(){
        var d = new Date(), p = function(n){ return (n < 10 ? "0" : "") + n; };
        return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate()) + " " + p(d.getHours()) + ":" + p(d.getMinutes());
      }

      /* ---------- text record, shared by copy, email, download and print ---------- */
      function recordText(){
        if (!S.R) return "ASK A VET™ Dilution Calculator\n\n" + (S.blocked || "Nothing calculated yet.");
        var R = S.R, fam = S.fam, L = [];
        L.push("ASK A VET™ DILUTION RECORD");
        L.push(stamp());
        L.push("");
        if (S.patient) L.push("Patient / case: " + S.patient);
        if (S.drug) L.push("Drug: " + S.drug);
        if (presetById(S.preset) && presetById(S.preset).noInjection) L.push("NOT FOR INJECTION");
        L.push("Intended use: " + S.prepUse);
        L.push("Stock concentration (C1): " + unitEcho(S.c1v, S.c1u) + " = " + fmtConc(S.c1, fam));
        if (S.mode !== "check") L.push("Target concentration (C2): " + fmtConc(R.finalConc, fam));
        L.push("Diluent: " + S.diluent);
        if (S.useC0) L.push("Same-substance concentration in diluent (C0): " + unitEcho(S.c0v, S.c0u) + " = " + fmtConc(S.c0, fam) + " — correction applied");
        L.push("Direct-draw safety threshold: " + fmt(S.minMeasure) + " mL");
        L.push("Selected device increment: " + fmt(S.measureIncrement) + " mL");
        if (S.measurement && S.measurement.accepted) L.push("Accepted device-mark adjustment: " + String(Number(S.measurement.exact.toPrecision(8))) + " mL calculated → " + fmt(S.measurement.nearest) + " mL used; achieved concentration " + fmtConcPrecise(S.measurement.achievedConc, fam));
        if (S.doseBasis) L.push("Dose / concentration basis: " + S.doseBasis + (S.basisConfirmed ? " — independently checked" : " — not confirmed"));
        if (S.outputBlockedReason){
          L.push("");
          L.push("STOP — NO PREPARATION RECORD OR LABEL GENERATED");
          L.push(S.outputBlockedReason);
          if (S.unmeasurable && !S.plan) buildSteps().forEach(function(b){ b.items.forEach(function(it, i){ L.push((i + 1) + ". " + strip(it)); }); });
          return L.join("\n");
        }
        L.push("");
        buildSteps().forEach(function(b){
          L.push(b.tag.toUpperCase());
          b.items.forEach(function(it, i){ L.push((b.letters ? "abcdefgh"[i] + "." : (i + 1) + ".") + " " + strip(it)); });
          L.push("");
        });
        L.push("RESULT");
        L.push((S.plan ? "Direct no-serial alternative (V1): " : "Direct stock volume (V1): ") + vol(R.v1) + " mL" + (S.unmeasurable ? "  <-- do not measure directly" : ""));
        if (S.plan){
          L.push("Actionable draw: " + fmt(S.plan.draw) + " mL from the final 1-in-" + S.plan.k + " intermediate (" + S.plan.stages + " staged 1-in-10 dilution" + (S.plan.stages > 1 ? "s" : "") + ")");
          L.push("Neat stock withdrawn for Stage 1: " + fmt(S.plan.stockWithdrawn) + " mL; neat-stock equivalent delivered to final preparation: " + fmt(S.plan.deliveredNeatEquivalent) + " mL");
        }
        L.push("Final volume: " + fmt(R.finalVol) + " mL");
        L.push("Final concentration: " + fmtConc(R.finalConc, fam) + (fam === "mass" ? " (" + fmt(R.finalConc / 10) + "% w/v)" : ""));
        L.push("Total substance in the container: " + fmtAmt(R.total, fam));
        L.push("Neat-stock-equivalent volume fraction: 1 in " + fmt(R.factor));
        if (S.dose) L.push("Dose (" + S.dose.basis + " basis): " + fmt(S.dose.wkg) + " kg at " + fmt(S.dose.rate) + " " + S.dose.rateU + " = " + fmtAmt(S.dose.amt, fam) + " = " + vol(S.dose.vol) + " mL of the diluted solution");
        L.push("");
        L.push("WORKING");
        strip(workingHTML().replace(/<\/div>/g, "\n")).split("\n").forEach(function(l){ if (l.trim()) L.push(l.trim()); });
        var flagged = sortWarns(S.warns).filter(function(w){ return w.lvl === "red" || w.lvl === "amber" || w.key === "preset"; });
        if (flagged.length){
          L.push("");
          L.push("CHECKS");
          flagged.forEach(function(w){
            L.push("- " + (w.lvl === "red" ? "CRITICAL: " : w.lvl === "amber" ? "CAUTION: " : "NOTE: ") + strip(w.t));
          });
        }
        L.push("");
        L.push("Prepared by ______________   Checked by ______________   Time ____________");
        L.push("");
        L.push("Generated by the ASK A VET™ Dilution Calculator by Dr Duncan Houston.");
        L.push("askavet.com/pages/dilution-calculator");
        L.push("Deterministic arithmetic on entered values. Verify against the product label before administration.");
        L.push("Use immediately; discard remainder unless a validated beyond-use time applies.");
        return L.join("\n");
      }
      function emailText(){
        if (!S.R) return recordText();
        var L = ["ASK A VET™ DILUTION RECORD", stamp(), ""];
        if (S.patient) L.push("Patient / case: " + S.patient);
        if (S.drug) L.push("Drug: " + S.drug);
        if (presetById(S.preset) && presetById(S.preset).noInjection) L.push("NOT FOR INJECTION");
        L.push("Intended use: " + S.prepUse);
        if (S.doseBasis) L.push("Dose / concentration basis: " + S.doseBasis + (S.basisConfirmed ? " — independently checked" : " — not confirmed"));
        if (S.dose) L.push("Dose volume: " + vol(S.dose.vol) + " mL for " + fmt(S.dose.wkg) + " kg at " + fmt(S.dose.rate) + " " + S.dose.rateU + " (" + S.dose.basis + " basis)");
        L.push(shortSummary());
        L.push("");
        buildSteps().forEach(function(b){
          L.push(b.tag.toUpperCase());
          b.items.forEach(function(it, i){ L.push((i + 1) + ". " + strip(it)); });
        });
        var flagged = sortWarns(S.warns).filter(function(w){ return w.lvl === "red" || w.lvl === "amber"; });
        if (flagged.length){
          L.push(""); L.push("SAFETY CHECKS");
          flagged.forEach(function(w){ L.push("- " + (w.lvl === "red" ? "CRITICAL: " : "CAUTION: ") + strip(w.t)); });
        }
        L.push("");
        L.push("Shareable calculator state (patient/case reference intentionally excluded):");
        L.push(location.href);
        L.push("");
        L.push("Verify the vial, arithmetic, compatibility, label and clinic protocol before administration.");
        return L.join("\n");
      }

      /* ---------- print sheet ---------- */
      function buildPrint(){
        var h = "<h1>Dilution record</h1>";
        h += '<div class="ps-sub">ASK A VET&trade; Dilution Calculator &nbsp;|&nbsp; ' + stamp() + "</div>";
        if (!S.R){ h += "<p>" + esc(S.blocked || "Nothing calculated.") + "</p>"; el("printSheet").innerHTML = h; return; }
        var R = S.R, fam = S.fam;
        if (S.outputBlockedReason){
          h += '<div class="ps-warn"><b>STOP — no preparation record or label generated.</b> ' + esc(S.outputBlockedReason) + '</div>';
          if (S.unmeasurable && !S.plan) buildSteps().forEach(function(b){ h += "<h2>" + esc(b.tag) + "</h2><ol>"; b.items.forEach(function(i){ h += "<li>" + i + "</li>"; }); h += "</ol>"; });
          h += '<div class="ps-foot">Correct the issue shown above and recalculate before preparing the dilution.</div>';
          el("printSheet").innerHTML = h;
          return;
        }
        h += '<div class="ps-grid">'
          + "<div><b>Drug</b> " + esc(S.drug || "________________") + "</div>"
          + "<div><b>Patient / case</b> " + esc(S.patient || "________________") + "</div>"
          + "<div><b>Intended use</b> " + esc(S.prepUse) + "</div>"
          + "<div><b>Stock concentration</b> " + esc(unitEcho(S.c1v, S.c1u)) + " (" + fmtConc(S.c1, fam) + ")</div>"
          + "<div><b>Diluent</b> " + esc(S.diluent) + "</div>"
          + (S.doseBasis ? "<div><b>Dose / concentration basis</b> " + esc(S.doseBasis) + (S.basisConfirmed ? " — checked" : " — not confirmed") + "</div>" : "")
          + (S.useC0 ? "<div><b>Diluent concentration (C0)</b> " + fmtConc(S.c0, fam) + " — corrected</div>" : "")
          + "<div><b>Final concentration</b> " + fmtConc(R.finalConc, fam) + "</div>"
          + "<div><b>Final volume</b> " + fmt(R.finalVol) + " mL</div>"
          + "<div><b>Measurement settings</b> minimum " + fmt(S.minMeasure) + " mL; increment " + fmt(S.measureIncrement) + " mL</div>"
          + (S.measurement && S.measurement.accepted ? "<div><b>Accepted nearest mark</b> " + String(Number(S.measurement.exact.toPrecision(8))) + " mL calculated → " + fmt(S.measurement.nearest) + " mL used; achieved " + fmtConcPrecise(S.measurement.achievedConc, fam) + "</div>" : "")
          + "</div>";
        sortWarns(S.warns).filter(function(w){ return w.lvl === "red"; }).forEach(function(w){
          h += '<div class="ps-warn"><b>Critical.</b> ' + strip(w.t) + "</div>";
        });
        buildSteps().forEach(function(b){
          h += "<h2>" + esc(b.tag) + "</h2><ol" + (b.letters ? ' type="a"' : "") + ">";
          b.items.forEach(function(i){ h += "<li>" + i + "</li>"; });
          h += "</ol>";
        });
        h += "<h2>Numbers</h2><table><tr><th>Item</th><th>Value</th><th>Notes</th></tr>"
          + "<tr><td>" + (S.plan ? "Direct no-serial alternative (V1)" : "Direct stock volume (V1)") + "</td><td>" + vol(R.v1) + " mL</td><td>" + (S.unmeasurable ? "do not measure directly" : (syringeFor(R.v1) ? syringeFor(R.v1) + " mL syringe" : "use a graduated container")) + "</td></tr>"
          + (S.unmeasurable && S.plan ? "<tr><td>Actionable draw from final 1 in " + S.plan.k + " intermediate</td><td>" + fmt(S.plan.draw) + " mL</td><td>after " + S.plan.stages + " staged 1-in-10 dilution" + (S.plan.stages > 1 ? "s" : "") + "</td></tr>" : "")
          + (S.plan ? "<tr><td>Stage 1 neat stock withdrawn</td><td>" + fmt(S.plan.stockWithdrawn) + " mL</td><td>" + fmt(S.plan.deliveredNeatEquivalent) + " mL neat-stock equivalent reaches the final preparation; account for all waste</td></tr>" : "")
          + (S.mode === "bag"
              ? (S.bagMethod === "remove"
                  ? "<tr><td>Removed from the bag first</td><td>" + ((S.unmeasurable && S.plan) ? fmt(S.plan.draw) : fmt(R.v1)) + " mL</td><td>discard it, the bag stays at " + fmt(R.finalVol) + " mL</td></tr>"
                  : "<tr><td>Diluent added</td><td>0 mL</td><td>the drug goes into the bag as it is</td></tr>")
              : "<tr><td>Final-step diluent added</td><td>" + fmt(S.plan ? R.finalVol - S.plan.draw : R.diluent) + " mL</td><td>" + (R.diluent > 1e-10 ? esc(S.diluent) : "none; use neat") + "</td></tr>")
          + "<tr><td>Final volume (V2)</td><td>" + fmt(R.finalVol) + " mL</td><td>includes the drug</td></tr>"
          + "<tr><td>Final concentration (C2)</td><td>" + fmtConc(R.finalConc, fam) + "</td><td>" + (fam === "mass" ? fmt(R.finalConc / 10) + "% w/v" : "") + "</td></tr>"
          + "<tr><td>Total substance present</td><td>" + fmtAmt(R.total, fam) + "</td><td>neat-stock-equivalent fraction 1 in " + fmt(R.factor) + "</td></tr>"
          + (S.dose ? "<tr><td>Dose for " + fmt(S.dose.wkg) + " kg at " + fmt(S.dose.rate) + " " + S.dose.rateU + "</td><td>" + vol(S.dose.vol) + " mL</td><td>" + fmtAmt(S.dose.amt, fam) + " on " + esc(S.dose.basis) + " basis</td></tr>" : "")
          + "</table>";
        h += '<h2>Working</h2><div class="ps-box">' + workingHTML().replace(/class="step"/g, 'style="font-family:monospace;font-size:11px;margin:2px 0"') + "</div>";
        sortWarns(S.warns).filter(function(w){ return w.lvl === "amber" || (w.key === "preset" && w.lvl !== "red"); }).forEach(function(w){
          h += '<div class="ps-box"><b>' + (w.lvl === "amber" ? "Caution." : "Note.") + "</b> " + strip(w.t) + "</div>";
        });
        h += '<h2>Labels, cut and attach</h2><div class="ps-labels">';
        var labPreset = presetById(S.preset);
        var lab = "<b>" + (labPreset && labPreset.noInjection ? "NOT FOR INJECTION — " : "") + esc(S.drug || "Diluted drug") + " " + fmtConc(R.finalConc, fam) + "</b>"
                + (S.patient ? "Patient/case: " + esc(S.patient) + "<br>" : "")
                + fmtAmt(R.total, fam) + " in " + fmt(R.finalVol) + " mL<br>"
                + (R.diluent > 1e-10 || S.mode === "bag" ? "Diluent: " + esc(S.diluent) : "Use neat; no diluent")
                + (S.doseBasis ? "<br>Basis: " + esc(S.doseBasis) : "")
                + "<br>Use: " + esc(S.prepUse) + "<br>Made ____/____ ____:____ &nbsp; Init ______<br>Use immediately; discard remainder unless a validated beyond-use time applies.";
        h += '<div class="ps-label">' + lab + '</div><div class="ps-label">' + lab + "</div>";
        h += '<div class="ps-label">' + lab + '</div><div class="ps-label">' + lab + "</div></div>";
        h += '<div class="ps-sign"><div>Prepared by</div><div>Checked by</div><div>Date / time</div></div>';
        h += '<div class="ps-foot">Generated by the ASK A VET&trade; Dilution Calculator by Dr Duncan Houston, askavet.com/pages/dilution-calculator. Deterministic arithmetic on entered values. For use by veterinary professionals: verify against the product label and your clinic protocol before administration.</div>';
        el("printSheet").innerHTML = h;
      }

      /* ---------- shareable state ---------- */
      function buildHash(){
        var p = [];
        var add = function(k, v){ if (v !== "" && v !== null && v !== undefined) p.push(k + "=" + encodeURIComponent(v)); };
        add("m", mode);
        add("dn", el("drugName").value.trim());
        add("c1", el("c1").value); add("c1u", el("c1u").value);
        add("c2", el("c2").value); add("c2u", el("c2u").value);
        add("v2", el("v2").value);
        add("bg", el("bagVol").value); add("bm", bagMethod); add("bt", bagTarget);
        add("bmv", el("bagMass").value); add("bmu", el("bagMassU").value);
        add("k1", el("ckV1").value); add("kd", el("ckDil").value);
        add("dl", el("diluent").value);
        add("od", el("otherDiluent").value.trim());
        add("pu", el("prepUse").value);
        add("ss", el("sameSolute").checked ? "1" : "0");
        add("c0", el("c0").value); add("c0u", el("c0u").value);
        add("mm", el("minMeasure").value);
        add("mi", el("measureIncrement").value);
        add("wt", el("wt").value); add("wu", wtU);
        add("dv", el("doseVal").value); add("du", el("doseU").value);
        add("db", el("doseBasis").value.trim());
        if (curPreset) add("p", curPreset);
        return "#" + p.join("&");
      }
      function writeHashNow(){
        try { history.replaceState(null, "", location.pathname + location.search + buildHash()); } catch(e){}
      }
      function writeHashSoon(){
        if (booting) return;
        clearTimeout(hashTimer);
        hashTimer = setTimeout(writeHashNow, 400);
      }
      function readHash(){
        var raw = location.hash.replace(/^#/, "");
        if (!raw) return false;
        var o = {}, ok = false;
        raw.split("&").forEach(function(kv){
          var i = kv.indexOf("=");
          if (i > 0){
            try { o[kv.slice(0, i)] = decodeURIComponent(kv.slice(i + 1).replace(/\+/g, " ")); }
            catch(e) { /* Ignore malformed fragments instead of aborting the calculator. */ }
          }
        });
        var setIf = function(id, k){
          if (o[k] === undefined || !el(id)) return;
          var node = el(id), old = node.value;
          node.value = o[k];
          if (node.tagName === "SELECT" && node.value === "") node.value = old;
          else ok = true;
        };
        if (o.bm === "remove" || o.bm === "add"){ bagMethod = o.bm; segSet("bagMethod", "data-m", bagMethod); ok = true; }
        if (o.bt === "conc" || o.bt === "mass"){
          bagTarget = o.bt; segSet("bagTarget", "data-t", bagTarget);
          el("bagMassWrap").classList.toggle("hide", bagTarget !== "mass");
          ok = true;
        }
        if (o.wu === "kg" || o.wu === "g" || o.wu === "lb"){ wtU = o.wu; segSet("wtUnit", "data-u", wtU); ok = true; }
        el("allowRounding").checked = false;
        if (o.m && MODEHELP[o.m]){ setMode(o.m); ok = true; }   /* after bagTarget, so the target field visibility is right */
        setIf("drugName", "dn");
        setIf("c1", "c1"); setIf("c1u", "c1u");
        setIf("c2", "c2"); setIf("c2u", "c2u");
        setIf("v2", "v2"); setIf("bagVol", "bg");
        setIf("bagMass", "bmv"); setIf("bagMassU", "bmu");
        setIf("ckV1", "k1"); setIf("ckDil", "kd");
        setIf("diluent", "dl");
        setIf("otherDiluent", "od"); setIf("prepUse", "pu");
        setIf("c0", "c0"); setIf("c0u", "c0u"); setIf("minMeasure", "mm"); setIf("measureIncrement", "mi");
        if (o.ss === "1" || o.ss === "0"){
          el("sameSolute").checked = o.ss === "1";
          el("sameSolute").dataset.user = "1";
          ok = true;
        }
        setIf("wt", "wt"); setIf("doseVal", "dv"); setIf("doseU", "du"); setIf("doseBasis", "db");
        el("basisConfirm").checked = false;
        if (o.wt || o.dv || o.db) openDose(true);
        if (o.p && presetById(o.p)){
          var pn = presetById(o.p);
          var canonical = sigNow() === sigForPreset(pn);
          var routeOK = el("prepUse").value === (pn.use || "Injection or infusion");
          var diluentOK = presetDiluentOK(pn, el("diluent").value, effectiveDiluent());
          if (canonical && routeOK && diluentOK){
            curPreset = o.p;
            el("presetNote").innerHTML = "<b>" + esc(pn.n) + ".</b> " + esc(pn.note);
            el("presetNote").classList.remove("hide");
            if (!o.db) el("doseBasis").value = pn.basis || pn.drug;
            presetSig = sigNow();
          }
        }
        refreshDiluentFields(false);
        setBagHint(); setDilHint();
        segSet("bagChips", "data-v", el("bagVol").value);
        return ok;
      }

      /* ---------- output actions ---------- */
      function fallbackCopy(txt){
        var ta = root.ownerDocument.createElement("textarea");
        ta.value = txt; ta.style.position = "fixed"; ta.style.opacity = "0";
        root.appendChild(ta); ta.select();
        var ok = false;
        try { ok = document.execCommand("copy"); } catch(e){}
        root.removeChild(ta);
        return ok;
      }
      function copyText(txt, msg){
        if (navigator.clipboard && navigator.clipboard.writeText){
          navigator.clipboard.writeText(txt).then(function(){ toast(msg); }, function(){ toast(fallbackCopy(txt) ? msg : "Copy failed — select the record manually"); });
        } else { toast(fallbackCopy(txt) ? msg : "Copy failed — select the record manually"); }
      }
      function slug(){
        var base = (S.drug || "dilution").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
        return "askavet-" + (base || "dilution") + "-" + stamp().replace(/[: ]/g, "-");
      }
      function openDose(open){
        el("doseBlock").classList.toggle("hide", !open);
        el("doseToggle").textContent = open ? "Hide" : "Show";
        el("doseToggle").setAttribute("aria-expanded", open ? "true" : "false");
      }

      /* ---------- events ---------- */
      function bind(){
        Array.prototype.forEach.call(el("mode").children, function(b){
          b.addEventListener("click", function(){
            el("allowRounding").checked = false;
            setMode(b.getAttribute("data-m"));
            if (curPreset) presetSig = sigNow();   /* a mode switch is not a change of drug */
            calc();
          });
        });
        Array.prototype.forEach.call(el("bagMethod").children, function(b){
          b.addEventListener("click", function(){ el("allowRounding").checked = false; bagMethod = b.getAttribute("data-m"); segSet("bagMethod", "data-m", bagMethod); setBagHint(); calc(); });
        });
        Array.prototype.forEach.call(el("bagTarget").children, function(b){
          b.addEventListener("click", function(){
            el("allowRounding").checked = false;
            bagTarget = b.getAttribute("data-t");
            segSet("bagTarget", "data-t", bagTarget);
            el("bagMassWrap").classList.toggle("hide", bagTarget !== "mass");
            el("targetWrap").classList.toggle("hide", bagTarget === "mass");
            calc();
          });
        });
        Array.prototype.forEach.call(el("wtUnit").children, function(b){
          b.addEventListener("click", function(){
            var next = b.getAttribute("data-u");
            if (next === wtU) return;
            var v = num("wt");
            if (isFinite(v) && v > 0 && document.activeElement !== el("wt")){
              var kg = wtU === "lb" ? v / LB : (wtU === "g" ? v / 1000 : v);
              var converted = next === "lb" ? kg * LB : (next === "g" ? kg * 1000 : kg);
              el("wt").value = String(Number(converted.toPrecision(10)));
            }
            wtU = next; segSet("wtUnit", "data-u", wtU); calc();
          });
        });
        ["c1","c2","c0","v2","bagVol","bagMass","ckV1","ckDil","wt","doseVal","doseBasis","drugName","patientRef","otherDiluent"].forEach(function(id){
          el(id).addEventListener("input", function(){
            if (["c1","c2","c0","v2","bagVol","bagMass","ckV1","ckDil","drugName","otherDiluent"].indexOf(id) !== -1) el("allowRounding").checked = false;
            if (id === "c1" || id === "c2") clearFactorChips();
            if (id === "c1" || id === "c2" || id === "drugName" || id === "doseBasis") el("basisConfirm").checked = false;
            if (id === "c0"){ el("sameSolute").dataset.user = "1"; el("sameSolute").dataset.auto = ""; }
            if (id === "bagVol") segSet("bagChips", "data-v", el("bagVol").value);
            calc();
          });
        });
        ["c1u","c2u","c0u","bagMassU","doseU","prepUse","minMeasure","measureIncrement"].forEach(function(id){
          el(id).addEventListener("change", function(){
            if (["c1u","c2u","c0u","bagMassU","prepUse","minMeasure","measureIncrement"].indexOf(id) !== -1) el("allowRounding").checked = false;
            if (id === "c1u" || id === "c2u") clearFactorChips();
            if (id === "c1u" || id === "c2u" || id === "doseU") el("basisConfirm").checked = false;
            if (id === "c0u"){ el("sameSolute").dataset.user = "1"; el("sameSolute").dataset.auto = ""; }
            refreshDiluentFields(false); setDilHint(); calc();
          });
        });
        el("diluent").addEventListener("change", function(){
          el("allowRounding").checked = false;
          el("sameSolute").dataset.user = "";
          el("sameSolute").dataset.auto = "";
          refreshDiluentFields(false); setDilHint(); calc();
        });
        el("sameSolute").addEventListener("change", function(){
          el("allowRounding").checked = false;
          el("sameSolute").dataset.user = "1";
          el("sameSolute").dataset.auto = "";
          refreshDiluentFields(false); calc();
        });
        el("basisConfirm").addEventListener("change", calc);
        el("allowRounding").addEventListener("change", calc);
        Array.prototype.forEach.call(el("v2Chips").children, function(b){
          b.addEventListener("click", function(){ el("allowRounding").checked = false; el("v2").value = b.getAttribute("data-v"); clearFactorChips(); calc(); });
        });
        Array.prototype.forEach.call(el("bagChips").children, function(b){
          b.addEventListener("click", function(){
            el("allowRounding").checked = false;
            el("bagVol").value = b.getAttribute("data-v");
            segSet("bagChips", "data-v", b.getAttribute("data-v"));
            calc();
          });
        });
        Array.prototype.forEach.call(el("factorChips").children, function(b){
          b.addEventListener("click", function(){
            el("allowRounding").checked = false;
            el("basisConfirm").checked = false;
            var f = parseFloat(b.getAttribute("data-f"));
            var c1 = toBase(num("c1"), el("c1u").value);
            if (!isFinite(c1) || c1 <= 0) return;
            el("c2u").value = el("c1u").value;
            var target = (UMAP[el("c1u").value].ratio) ? num("c1") * f : num("c1") / f;
            el("c2").value = Number(target.toPrecision(6));
            calc();
            segSet("factorChips", "data-f", String(f));
          });
        });
        Array.prototype.forEach.call(el("ckChips").children, function(b){
          b.addEventListener("click", function(){
            el("allowRounding").checked = false;
            var r = parseFloat(b.getAttribute("data-r"));
            var v1 = num("ckV1");
            if (!isFinite(v1) || v1 <= 0){ v1 = 1; el("ckV1").value = 1; }
            el("ckDil").value = Number((v1 * (r - 1)).toPrecision(6));
            calc();
          });
        });
        el("presetSearch").addEventListener("input", renderPresets);
        el("presetClear").addEventListener("click", function(){ el("presetSearch").value = ""; clearPreset(); calc(); });
        el("doseToggle").addEventListener("click", function(){ openDose(el("doseBlock").classList.contains("hide")); });

        el("btnPrint").addEventListener("click", function(){ buildPrint(); window.print(); });
        /* the browser's own print command has to get the same sheet */
        window.addEventListener("beforeprint", buildPrint);
        if (window.matchMedia){
          var mq = window.matchMedia("print");
          if (mq.addEventListener) mq.addEventListener("change", function(e){ if (e.matches) buildPrint(); });
          else if (mq.addListener) mq.addListener(function(e){ if (e.matches) buildPrint(); });
        }
        el("btnCopy").addEventListener("click", function(){ copyText(recordText(), "Copied to clipboard"); });
        el("btnDownload").addEventListener("click", function(){
          var blob = new Blob([recordText()], {type:"text/plain;charset=utf-8"});
          var a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = slug() + ".txt";
          document.body.appendChild(a); a.click();
          setTimeout(function(){ URL.revokeObjectURL(a.href); document.body.removeChild(a); }, 500);
          toast("Downloaded");
        });
        el("btnEmail").addEventListener("click", function(){
          writeHashNow();
          var subj = "Dilution record" + (S.drug ? ": " + S.drug : "");
          var body = emailText();
          var href = "mailto:?subject=" + encodeURIComponent(subj) + "&body=" + encodeURIComponent(body);
          if (href.length > 7000){ copyText(recordText(), "Email is too long; full record copied instead"); return; }
          window.location.href = href;
        });
        el("btnShare").addEventListener("click", function(){
          writeHashNow();
          var url = location.href;
          var title = "ASK A VET Dilution Calculator" + (S.drug ? ": " + S.drug : "");
          if (navigator.share){
            navigator.share({title:title, text:shortSummary(), url:url}).then(function(){}, function(err){ if (!err || err.name !== "AbortError") copyText(url, "Link copied"); });
          } else {
            copyText(url, "Link copied");
          }
        });
        el("resetAll").addEventListener("click", function(){
          location.hash = "";
          location.reload();
        });
      }
      function clearFactorChips(){ segSet("factorChips", "data-f", "__none__"); }
      function setBagHint(){
        el("bagMethodHint").innerHTML = (bagMethod === "remove")
          ? "Remove the calculated add-volume first, then replace it with stock or intermediate so the bag stays at the entered volume. Use the actual or protocol-approved starting volume."
          : "Add without removing fluid. The final volume increases, so the calculator re-solves the mass balance and the new volume must be written on the bag.";
      }
      function setDilHint(){
        var d = el("diluent").value;
        var m = {
          "0.9% sodium chloride":"Commonly used, but compatibility and stability remain product-specific. It already contains sodium chloride, which matters when diluting concentrated saline.",
          "Sterile water for injection":"Sterile but hypotonic. Confirm final tonicity and the product-approved route before parenteral use.",
          "Lactated Ringer's (Hartmann's)":"Contains calcium, sodium chloride and about 4 mEq/L potassium. Confirm product-specific compatibility; the calculator applies a potassium-background correction when the drug is identified as KCl.",
          "5% dextrose":"Contains 50 mg/mL dextrose. Confirm compatibility, and apply the same-substance correction when diluting concentrated dextrose.",
          "Cooled boiled water":"Not sterile. Never use for an injectable preparation.",
          "Other diluent":"Enter the exact product name and confirm it against the product label or a validated clinic protocol."
        };
        el("dilHint").textContent = m[d] || "";
      }

      /* ---------- static reference table integrity ---------- */
      function checkTable(){
        var body = el("presetTableBody");
        if (!body) return;
        var rows = body.querySelectorAll("tr"), drift = [];
        if (rows.length !== PRESETS.length) drift.push("row count " + rows.length + " vs " + PRESETS.length);
        for (var i = 0; i < Math.min(rows.length, PRESETS.length); i++){
          var p = PRESETS[i], c = rows[i].children;
          var c1 = toBase(p.c1[0], p.c1[1]), c2 = toBase(p.c2[0], p.c2[1]);
          var r = (p.mode === "bag") ? calcBag({c1:c1, bag:p.bag, method:"remove", target:"conc", c2:c2}) : calcQuick(c1, c2, p.v2);
          var want = [p.n, unitEcho(p.c1[0], p.c1[1]), unitEcho(p.c2[0], p.c2[1]),
                      fmt(r.finalVol) + " mL", fmt(r.v1) + " mL", fmt(r.diluent) + " mL", p.why];
          if (c.length !== want.length) drift.push(p.id + " has " + c.length + " cells, expected " + want.length);
          for (var j = 0; j < want.length; j++){
            var got = (c[j] ? c[j].textContent : "").replace(/\s+/g, " ").trim();
            if (got !== want[j].replace(/\s+/g, " ").trim()) drift.push(p.id + " col" + (j + 1) + ': "' + got + '" vs "' + want[j] + '"');
          }
        }
        if (drift.length){
          var w = document.createElement("div");
          w.className = "warn red";
          w.innerHTML = "<b>Reference table drift.</b> The printed table below no longer matches the live calculator data (" + drift.length + " mismatch" + (drift.length > 1 ? "es" : "") + "). Trust the calculator, not the table. First: " + esc(drift[0]);
          body.parentNode.parentNode.insertBefore(w, body.parentNode);
        }
      }

      /* ---------- init ---------- */
      fillUnitSelect(el("c1u"), UNITS, "mgml");
      fillUnitSelect(el("c2u"), UNITS, "mgml");
      fillUnitSelect(el("c0u"), UNITS, "mgml");
      fillUnitSelect(el("bagMassU"), AMTS, "meq");
      fillUnitSelect(el("doseU"), DOSES, "mgkg");
      setupRatio("c1Wrap", "c1");
      setupRatio("c2Wrap", "c2");
      setupRatio("c0Wrap", "c0");
      setMode("quick");
      (function(){ var c = el("presetCount"); if (c) c.textContent = PRESETS.length + " vet presets, optional starting point"; })();
      setBagHint();
      setDilHint();
      bind();
      var restored = readHash();
      refreshDiluentFields(false);
      renderPresets();
      booting = false;
      calc();
      checkTable();
      })();
    }
  }
  customElements.define(ELEMENT_NAME, DilutionCalculator);
})();
